/**
 * Elite Batch 15 Part 4 — Workflow Templates 1408–1427
 * Regulatory change impact assessment, regulatory monitoring agents, filing deadline
 * monitors, renewal reminder drips, report formatting, report scheduling, resource
 * allocation optimization, resource request bots, risk assessment bots, risk register
 * updates, SLA monitoring, SMS appointment reminders, SOP document generation,
 * scheduled backup alerts, scope change requests, scope creep detection, service
 * feedback emails, service status page bots, shipment tracking, and Slack notification hubs.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 15 PART 4 — TEMPLATES 1408–1427
// =============================================================================

export const ELITE_BATCH15_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1408. REGULATORY CHANGE IMPACT ASSESSOR (9 nodes)
  // ===================================================================

  "Regulatory Change Impact Assessor": {
    summary:
      "Receives regulatory alert webhooks, extracts regulation details with AI, parses regulatory language into structured requirements, maps business impact areas in code, runs gap analysis against current compliance posture, computes risk scores, generates an AI action plan, notifies stakeholders by email, and creates compliance tasks in Asana.",
    nodes: [
      trigger("Webhook: Regulatory alert received", "webhook_app", {
        source: "regulatory_feed",
        event: "regulation.published",
        howItStarts:
          "Fires when a new regulatory change alert is received from a compliance monitoring service (e.g., Thomson Reuters Regulatory Intelligence, LexisNexis, or a government RSS feed)",
        setup:
          "Configure webhook from your regulatory monitoring service. Set up OpenAI API key for regulatory parsing and action plan generation. Configure Asana API token for task creation. Provide email settings for stakeholder notification.",
        inputFields: [],
      }),

      // 2. AI extract regulation data
      aiNode(
        "2",
        "AI: Extract regulation details",
        280,
        "You are a regulatory compliance analyst. Extract the regulation details from the raw webhook payload regardless of format. Find the regulation title, issuing authority, regulation ID or reference number, effective date, jurisdiction, affected industries, summary of the change, compliance deadline, and the source URL. Respond JSON: {regulation_title: string, issuing_authority: string, regulation_id: string, effective_date: string, jurisdiction: string, affected_industries: string, change_summary: string, compliance_deadline: string, source_url: string, full_text_excerpt: string}",
        "Raw regulatory alert payload: {webhook_payload}",
        [
          { field: "regulation_title", as: "regulation_title", type: "string" },
          {
            field: "issuing_authority",
            as: "issuing_authority",
            type: "string",
          },
          { field: "regulation_id", as: "regulation_id", type: "string" },
          { field: "effective_date", as: "effective_date", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          {
            field: "affected_industries",
            as: "affected_industries",
            type: "string",
          },
          { field: "change_summary", as: "change_summary", type: "string" },
          {
            field: "compliance_deadline",
            as: "compliance_deadline",
            type: "string",
          },
          { field: "source_url", as: "source_url", type: "string" },
          {
            field: "full_text_excerpt",
            as: "full_text_excerpt",
            type: "string",
          },
        ],
      ),

      // 3. AI regulatory parser — structured requirements
      aiNode(
        "3",
        "AI: Parse regulatory requirements",
        520,
        "You are a regulatory language specialist. Parse the regulation change into specific, actionable compliance requirements. Identify mandatory vs. recommended actions, new reporting obligations, data handling changes, operational process changes, and technology requirements. Respond JSON: {mandatory_requirements: string, recommended_actions: string, reporting_obligations: string, data_handling_changes: string, process_changes: string, technology_requirements: string, penalty_for_noncompliance: string, total_requirements_count: number}",
        "Regulation: {regulation_title} ({regulation_id}). Authority: {issuing_authority}. Jurisdiction: {jurisdiction}. Summary: {change_summary}. Excerpt: {full_text_excerpt}. Deadline: {compliance_deadline}.",
        [
          {
            field: "mandatory_requirements",
            as: "mandatory_requirements",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "reporting_obligations",
            as: "reporting_obligations",
            type: "string",
          },
          {
            field: "data_handling_changes",
            as: "data_handling_changes",
            type: "string",
          },
          { field: "process_changes", as: "process_changes", type: "string" },
          {
            field: "technology_requirements",
            as: "technology_requirements",
            type: "string",
          },
          {
            field: "penalty_for_noncompliance",
            as: "penalty_for_noncompliance",
            type: "string",
          },
          {
            field: "total_requirements_count",
            as: "total_requirements_count",
            type: "number",
          },
        ],
      ),

      // 4. Business impact mapper runCode
      n("4", "runCode", "Map business impact areas", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map regulatory requirements to business impact areas
const reqCount = parseInt(vars.total_requirements_count || "0", 10);
const hasDataChanges = (vars.data_handling_changes || "").toLowerCase() !== "none" && vars.data_handling_changes !== "";
const hasProcessChanges = (vars.process_changes || "").toLowerCase() !== "none" && vars.process_changes !== "";
const hasTechReqs = (vars.technology_requirements || "").toLowerCase() !== "none" && vars.technology_requirements !== "";
const hasReporting = (vars.reporting_obligations || "").toLowerCase() !== "none" && vars.reporting_obligations !== "";
const impactedDepts = [];
if (hasDataChanges) impactedDepts.push("IT/Security", "Legal", "Data Governance");
if (hasProcessChanges) impactedDepts.push("Operations", "HR", "Compliance");
if (hasTechReqs) impactedDepts.push("Engineering", "IT Infrastructure");
if (hasReporting) impactedDepts.push("Finance", "Compliance Reporting");
const uniqueDepts = [...new Set(impactedDepts)];
const impactScope = uniqueDepts.length >= 5 ? "ENTERPRISE-WIDE" : uniqueDepts.length >= 3 ? "MULTI-DEPARTMENT" : "TARGETED";
const estimatedEffort = reqCount <= 3 ? "LOW (1-2 weeks)" : reqCount <= 7 ? "MEDIUM (2-6 weeks)" : "HIGH (6+ weeks)";
return {
  impacted_departments: uniqueDepts.join(", "),
  department_count: uniqueDepts.length,
  impact_scope: impactScope,
  estimated_effort: estimatedEffort,
  has_data_impact: hasDataChanges,
  has_process_impact: hasProcessChanges,
  has_tech_impact: hasTechReqs,
  has_reporting_impact: hasReporting,
};`,
          outputs: [
            { name: "impacted_departments", type: "string" },
            { name: "department_count", type: "number" },
            { name: "impact_scope", type: "string" },
            { name: "estimated_effort", type: "string" },
            { name: "has_data_impact", type: "boolean" },
            { name: "has_process_impact", type: "boolean" },
            { name: "has_tech_impact", type: "boolean" },
            { name: "has_reporting_impact", type: "boolean" },
          ],
        },
      }),

      // 5. Gap analysis engine runCode
      n("5", "runCode", "Run compliance gap analysis", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify compliance gaps against current posture
const mandatoryReqs = (vars.mandatory_requirements || "").split(",").map(r => r.trim()).filter(Boolean);
const gapItems = [];
mandatoryReqs.forEach((req, i) => {
  gapItems.push({ requirement: req, gap_id: "GAP-" + (i + 1), status: "OPEN", priority: i < 3 ? "HIGH" : "MEDIUM" });
});
const totalGaps = gapItems.length;
const highPriorityGaps = gapItems.filter(g => g.priority === "HIGH").length;
const gapSummary = gapItems.map(g => g.gap_id + ": " + g.requirement + " [" + g.priority + "]").join("; ");
const complianceReadiness = totalGaps === 0 ? "COMPLIANT" : highPriorityGaps > 2 ? "AT_RISK" : "PARTIALLY_COMPLIANT";
return {
  total_gaps: totalGaps,
  high_priority_gaps: highPriorityGaps,
  gap_summary: gapSummary,
  compliance_readiness: complianceReadiness,
  assessment_date: new Date().toISOString(),
};`,
          outputs: [
            { name: "total_gaps", type: "number" },
            { name: "high_priority_gaps", type: "number" },
            { name: "gap_summary", type: "string" },
            { name: "compliance_readiness", type: "string" },
            { name: "assessment_date", type: "string" },
          ],
        },
      }),

      // 6. Risk scoring runCode
      n("6", "runCode", "Calculate regulatory risk score", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate composite regulatory risk score
const totalGaps = parseInt(vars.total_gaps || "0", 10);
const highGaps = parseInt(vars.high_priority_gaps || "0", 10);
const deptCount = parseInt(vars.department_count || "1", 10);
const scope = vars.impact_scope || "TARGETED";
const scopeMultiplier = scope === "ENTERPRISE-WIDE" ? 1.5 : scope === "MULTI-DEPARTMENT" ? 1.2 : 1.0;
const gapScore = Math.min(40, (totalGaps * 5) + (highGaps * 10));
const scopeScore = Math.min(30, deptCount * 5);
const penaltyScore = (vars.penalty_for_noncompliance || "").toLowerCase().includes("fine") ? 30 : 15;
const rawScore = Math.round((gapScore + scopeScore + penaltyScore) * scopeMultiplier);
const riskScore = Math.min(100, rawScore);
const riskLevel = riskScore >= 75 ? "CRITICAL" : riskScore >= 50 ? "HIGH" : riskScore >= 25 ? "MEDIUM" : "LOW";
const urgency = riskScore >= 60 ? "IMMEDIATE" : riskScore >= 35 ? "WITHIN_2_WEEKS" : "STANDARD";
return {
  regulatory_risk_score: riskScore,
  risk_level: riskLevel,
  urgency: urgency,
  risk_breakdown: "Gaps: " + gapScore + " + Scope: " + scopeScore + " + Penalty: " + penaltyScore + " (x" + scopeMultiplier + ")",
};`,
          outputs: [
            { name: "regulatory_risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "urgency", type: "string" },
            { name: "risk_breakdown", type: "string" },
          ],
        },
      }),

      // 7. AI action plan generator
      aiNode(
        "7",
        "AI: Generate compliance action plan",
        1480,
        "You are a senior compliance strategist. Generate a prioritized action plan to address the identified compliance gaps. For each gap, specify the responsible department, concrete action steps, resource requirements, and timeline. Include quick wins that can be completed before the compliance deadline. Respond JSON: {action_plan_summary: string, quick_wins: string, medium_term_actions: string, long_term_initiatives: string, resource_estimate: string, recommended_owner: string, milestone_dates: string}",
        "Regulation: {regulation_title} ({regulation_id}). Deadline: {compliance_deadline}. Risk level: {risk_level} (Score: {regulatory_risk_score}/100). Gaps: {gap_summary}. Impacted departments: {impacted_departments}. Scope: {impact_scope}. Effort: {estimated_effort}. Mandatory requirements: {mandatory_requirements}. Technology needs: {technology_requirements}. Process changes: {process_changes}.",
        [
          {
            field: "action_plan_summary",
            as: "action_plan_summary",
            type: "string",
          },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          {
            field: "medium_term_actions",
            as: "medium_term_actions",
            type: "string",
          },
          {
            field: "long_term_initiatives",
            as: "long_term_initiatives",
            type: "string",
          },
          {
            field: "resource_estimate",
            as: "resource_estimate",
            type: "string",
          },
          {
            field: "recommended_owner",
            as: "recommended_owner",
            type: "string",
          },
          { field: "milestone_dates", as: "milestone_dates", type: "string" },
        ],
      ),

      // 8. Stakeholder notification email
      emailNode(
        "8",
        "Notify stakeholders via email",
        1720,
        "{compliance_team_email}",
        "Regulatory Impact Alert: {regulation_title} — Risk Level {risk_level}",
        "<h2>Regulatory Change Impact Assessment</h2><p><strong>Regulation:</strong> {regulation_title} ({regulation_id})</p><p><strong>Authority:</strong> {issuing_authority} | <strong>Jurisdiction:</strong> {jurisdiction}</p><p><strong>Compliance Deadline:</strong> {compliance_deadline} | <strong>Urgency:</strong> {urgency}</p><hr/><p><strong>Risk Score:</strong> {regulatory_risk_score}/100 ({risk_level})</p><p><strong>Impact Scope:</strong> {impact_scope} — {impacted_departments}</p><p><strong>Compliance Readiness:</strong> {compliance_readiness} ({total_gaps} gaps, {high_priority_gaps} high priority)</p><hr/><h3>Action Plan</h3><p>{action_plan_summary}</p><p><strong>Quick Wins:</strong> {quick_wins}</p><p><strong>Medium-Term:</strong> {medium_term_actions}</p><p><strong>Recommended Owner:</strong> {recommended_owner}</p><p><strong>Resources:</strong> {resource_estimate}</p><p><a href='{source_url}'>View Full Regulation</a></p>",
      ),

      // 9. Compliance task creator — Asana
      n("9", "hitApi", "Create Asana compliance tasks", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[REG] {regulation_id}: {regulation_title}","notes":"Risk: {risk_level} ({regulatory_risk_score}/100)\\nDeadline: {compliance_deadline}\\nUrgency: {urgency}\\nScope: {impact_scope}\\nDepartments: {impacted_departments}\\n\\nGaps:\\n{gap_summary}\\n\\nAction Plan:\\n{action_plan_summary}\\n\\nQuick Wins: {quick_wins}\\nMedium-Term: {medium_term_actions}\\nResources: {resource_estimate}","projects":["{asana_compliance_project_id}"],"due_on":"{compliance_deadline}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1409. REGULATORY CHANGE MONITOR AGENT (8 nodes)
  // ===================================================================

  "Regulatory Change Monitor Agent": {
    summary:
      "Runs daily to fetch regulatory updates from multiple sources, filters for business relevance using AI, classifies change severity in code, evaluates threshold for immediate alerting, dispatches urgent alerts to Slack, compiles weekly digest emails for low-severity changes, and logs all changes to a tracking sheet.",
    nodes: [
      trigger("Schedule: Daily regulatory scan", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Fires every day at 7 AM to scan regulatory feeds for new changes, filter for relevance to your business, and route alerts based on severity",
        setup:
          "Configure OpenAI API key for relevance filtering. Provide regulatory feed API key (e.g., Federal Register API, EUR-Lex, or industry-specific feed). Set up Slack webhook for urgent alerts. Configure email for weekly digest. Set Google Sheets for change log.",
        inputFields: [],
      }),

      // 2. Multi-source fetcher API
      n("2", "hitApi", "Fetch regulatory feed updates", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.federalregister.gov/api/v1/documents.json?conditions[publication_date][gte]={yesterday_date}&per_page=50&order=newest",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "count", customName: "total_documents", type: "number" },
            { name: "results", customName: "raw_documents", type: "string" },
            {
              name: "results[0].title",
              customName: "latest_doc_title",
              type: "string",
            },
            {
              name: "results[0].abstract",
              customName: "latest_doc_abstract",
              type: "string",
            },
            {
              name: "results[0].document_number",
              customName: "latest_doc_number",
              type: "string",
            },
            {
              name: "results[0].html_url",
              customName: "latest_doc_url",
              type: "string",
            },
            {
              name: "results[0].agencies[0].name",
              customName: "latest_agency",
              type: "string",
            },
            {
              name: "results[0].publication_date",
              customName: "latest_pub_date",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI relevance filter
      aiNode(
        "3",
        "AI: Filter for business relevance",
        520,
        "You are a regulatory affairs analyst. Review the retrieved regulatory documents and determine which are relevant to the company's industry and operations. Score each on a relevance scale of 0-100. Identify the most impactful document. Respond JSON: {relevant_count: number, top_document_title: string, top_document_number: string, top_relevance_score: number, top_impact_summary: string, affected_business_areas: string, regulatory_type: string, action_required: boolean, severity: string}",
        "Total documents found: {total_documents}. Latest title: {latest_doc_title}. Abstract: {latest_doc_abstract}. Document number: {latest_doc_number}. Agency: {latest_agency}. Published: {latest_pub_date}. Our industries: financial services, technology, data privacy. Review and score for business relevance.",
        [
          { field: "relevant_count", as: "relevant_count", type: "number" },
          {
            field: "top_document_title",
            as: "top_document_title",
            type: "string",
          },
          {
            field: "top_document_number",
            as: "top_document_number",
            type: "string",
          },
          {
            field: "top_relevance_score",
            as: "top_relevance_score",
            type: "number",
          },
          {
            field: "top_impact_summary",
            as: "top_impact_summary",
            type: "string",
          },
          {
            field: "affected_business_areas",
            as: "affected_business_areas",
            type: "string",
          },
          { field: "regulatory_type", as: "regulatory_type", type: "string" },
          { field: "action_required", as: "action_required", type: "boolean" },
          { field: "severity", as: "change_severity", type: "string" },
        ],
      ),

      // 4. Change classification runCode
      n("4", "runCode", "Classify change severity level", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify regulatory change and determine routing
const relevanceScore = parseInt(vars.top_relevance_score || "0", 10);
const severity = (vars.change_severity || "low").toUpperCase();
const actionRequired = vars.action_required === "true" || vars.action_required === true;
const relevantCount = parseInt(vars.relevant_count || "0", 10);
let alertTier = "DIGEST";
if (severity === "CRITICAL" || (relevanceScore >= 80 && actionRequired)) alertTier = "IMMEDIATE";
else if (severity === "HIGH" || relevanceScore >= 60) alertTier = "URGENT";
else if (relevanceScore >= 30) alertTier = "DIGEST";
else alertTier = "LOG_ONLY";
const requiresImmediateAlert = alertTier === "IMMEDIATE" || alertTier === "URGENT";
return {
  alert_tier: alertTier,
  requires_immediate_alert: requiresImmediateAlert,
  classification_label: severity + " / " + alertTier + " (Score: " + relevanceScore + ")",
  relevant_document_count: relevantCount,
  scan_timestamp: new Date().toISOString(),
};`,
          outputs: [
            { name: "alert_tier", type: "string" },
            { name: "requires_immediate_alert", type: "boolean" },
            { name: "classification_label", type: "string" },
            { name: "relevant_document_count", type: "number" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 5. Threshold alert evaluator condition
      n("5", "condition", "Check: Requires immediate alert?", 1000, {
        conditionData: [
          {
            variable: "requires_immediate_alert",
            operator: "equals",
            value: "true",
          },
        ],
      }),

      // 6. Immediate alert dispatch Slack (True branch)
      slackNode(
        "6",
        "Dispatch immediate regulatory alert (Slack)",
        1240,
        ":rotating_light: *REGULATORY ALERT — {alert_tier}*\n*Document:* {top_document_title}\n*Number:* {top_document_number} | Agency: {latest_agency}\n*Severity:* {change_severity} | Relevance: {top_relevance_score}/100\n*Areas Affected:* {affected_business_areas}\n*Type:* {regulatory_type}\n\n*Impact:* {top_impact_summary}\n*Action Required:* {action_required}\n\n<{latest_doc_url}|View Full Document>",
        -220,
      ),

      // 7. Weekly digest generator email (False branch)
      emailNode(
        "7",
        "Queue for weekly regulatory digest",
        1240,
        "{compliance_digest_email}",
        "Weekly Regulatory Digest — {relevant_count} relevant changes found",
        "<h3>Regulatory Change Digest</h3><p><strong>Scan Date:</strong> {scan_timestamp}</p><p><strong>Documents Scanned:</strong> {total_documents} | <strong>Relevant:</strong> {relevant_count}</p><hr/><p><strong>Top Document:</strong> {top_document_title} ({top_document_number})</p><p><strong>Classification:</strong> {classification_label}</p><p><strong>Impact:</strong> {top_impact_summary}</p><p><strong>Areas:</strong> {affected_business_areas}</p><p><a href='{latest_doc_url}'>View Document</a></p>",
        380,
      ),

      // 8. Change log Sheets
      sheetsNode(
        "8",
        "Log regulatory changes (Sheets)",
        1480,
        "Regulatory_Changes",
        '[["{scan_timestamp}","{top_document_title}","{top_document_number}","{latest_agency}","{change_severity}","{top_relevance_score}","{alert_tier}","{affected_business_areas}","{regulatory_type}","{action_required}","{top_impact_summary}","{latest_doc_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1410. REGULATORY FILING DEADLINE MONITOR (7 nodes)
  // ===================================================================

  "Regulatory Filing Deadline Monitor": {
    summary:
      "Runs daily to sync filing deadlines from a tracking sheet, calculates days remaining for each filing, sends early warnings at 30 days, preparation reminders at 14 days, evaluates urgency at 7 days, and dispatches final SMS warnings at 48 hours before deadline.",
    nodes: [
      trigger("Schedule: Daily filing deadline check", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Fires every day at 8 AM to check all regulatory filing deadlines, calculate remaining time, and send escalating reminders as deadlines approach",
        setup:
          "Set up Google Sheets with your filing deadline tracker (columns: filing_name, filing_type, deadline_date, responsible_person, responsible_email, responsible_phone, status). Configure email for warnings. Set up SMS provider for final alerts.",
        inputFields: [],
      }),

      // 2. Deadline database sync Sheets
      n("2", "hitApi", "Fetch filing deadlines from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Filing_Deadlines!A2:H100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values[0][0]", customName: "filing_name", type: "string" },
            { name: "values[0][1]", customName: "filing_type", type: "string" },
            {
              name: "values[0][2]",
              customName: "deadline_date",
              type: "string",
            },
            {
              name: "values[0][3]",
              customName: "responsible_person",
              type: "string",
            },
            {
              name: "values[0][4]",
              customName: "responsible_email",
              type: "string",
            },
            {
              name: "values[0][5]",
              customName: "responsible_phone",
              type: "string",
            },
            {
              name: "values[0][6]",
              customName: "filing_status",
              type: "string",
            },
            {
              name: "values.length",
              customName: "total_filings",
              type: "number",
            },
          ],
        },
      }),

      // 3. Countdown calculator runCode
      n("3", "runCode", "Calculate days remaining for each filing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate days until deadline and determine alert tier
const deadline = new Date(vars.deadline_date);
const now = new Date();
const diffMs = deadline.getTime() - now.getTime();
const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
const hoursRemaining = Math.ceil(diffMs / (1000 * 60 * 60));
const status = vars.filing_status || "pending";
let alertTier = "NONE";
if (status === "completed") alertTier = "NONE";
else if (hoursRemaining <= 48) alertTier = "FINAL_WARNING";
else if (daysRemaining <= 7) alertTier = "URGENT";
else if (daysRemaining <= 14) alertTier = "PREPARATION";
else if (daysRemaining <= 30) alertTier = "EARLY_WARNING";
const isOverdue = daysRemaining < 0;
return {
  days_remaining: daysRemaining,
  hours_remaining: hoursRemaining,
  alert_tier: alertTier,
  is_overdue: isOverdue,
  deadline_formatted: deadline.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
};`,
          outputs: [
            { name: "days_remaining", type: "number" },
            { name: "hours_remaining", type: "number" },
            { name: "alert_tier", type: "string" },
            { name: "is_overdue", type: "boolean" },
            { name: "deadline_formatted", type: "string" },
          ],
        },
      }),

      // 4. Early warning (30d) email
      emailNode(
        "4",
        "Send 30-day early warning email",
        760,
        "{responsible_email}",
        "30-Day Filing Reminder: {filing_name} due {deadline_formatted}",
        "<p>Hi {responsible_person},</p><p>This is an early reminder that the <strong>{filing_name}</strong> ({filing_type}) filing is due in <strong>{days_remaining} days</strong> on <strong>{deadline_formatted}</strong>.</p><p>Please begin gathering the required documentation and data. If you need assistance, reach out to the compliance team.</p><p><strong>Status:</strong> {filing_status}</p>",
      ),

      // 5. Preparation reminder (14d) email
      emailNode(
        "5",
        "Send 14-day preparation reminder",
        1000,
        "{responsible_email}",
        "ACTION NEEDED: {filing_name} due in {days_remaining} days",
        "<p>Hi {responsible_person},</p><p><strong>{filing_name}</strong> ({filing_type}) is due in <strong>{days_remaining} days</strong> on {deadline_formatted}.</p><p>Please ensure all data is compiled and the filing draft is under review. This is the preparation phase — the filing should be in near-final form within the next week.</p><p><strong>Current Status:</strong> {filing_status}</p><p>Reply to this email if you need an extension or assistance.</p>",
      ),

      // 6. Urgency escalation (7d) condition
      n("6", "condition", "Check: Filing deadline within 48 hours?", 1240, {
        conditionData: [
          {
            variable: "alert_tier",
            operator: "equals",
            value: "FINAL_WARNING",
          },
        ],
      }),

      // 7. Final warning (48h) SMS
      smsNode(
        "7",
        "Send 48-hour final deadline SMS",
        1480,
        "{responsible_phone}",
        "URGENT: {filing_name} filing is due in {hours_remaining} hours ({deadline_formatted}). Status: {filing_status}. Submit immediately or contact compliance for emergency extension. Ref: {filing_type}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 1411. RENEWAL REMINDER DRIP (8 nodes)
  // ===================================================================

  "Renewal Reminder Drip": {
    summary:
      "Runs daily to scan HubSpot for contacts approaching renewal windows, enriches customer data with account details, personalizes renewal messaging with AI, sends a 90-day advance email, waits 30 days, sends a 60-day follow-up email, and logs engagement analytics to Sheets.",
    nodes: [
      trigger("Schedule: Daily renewal window scan", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Fires every weekday at 9 AM to identify customers with contracts or subscriptions approaching their renewal date and initiate a personalized drip sequence",
        setup:
          "Configure HubSpot API token. Set up OpenAI API key for content personalization. Configure email delivery. Set Google Sheets for engagement analytics. Define renewal window (default: 90 days before expiry).",
        inputFields: [],
      }),

      // 2. Renewal window scanner HubSpot
      hubspotGetNode(
        "2",
        "Scan HubSpot for upcoming renewals",
        280,
        "crm/v3/objects/deals/search",
        [
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "renewal_date",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_object_id",
            customName: "deal_id",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "deal_owner_id",
            type: "string",
          },
          { name: "total", customName: "total_renewals_found", type: "number" },
        ],
      ),

      // 3. Customer data enrichment HubSpot
      hubspotGetNode(
        "3",
        "Enrich customer account from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].properties.firstname",
            customName: "customer_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "customer_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "customer_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "customer_title",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 4. AI content personalization
      aiNode(
        "4",
        "AI: Personalize renewal messaging",
        760,
        "You are a customer retention specialist. Create personalized renewal email content based on the customer's account data, deal value, and relationship history. Write a warm, consultative tone that emphasizes value delivered and future benefits. Include a personalized value proposition and a clear call-to-action to schedule a renewal conversation. Respond JSON: {email_subject_90d: string, email_body_90d: string, email_subject_60d: string, email_body_60d: string, value_proposition: string, personalized_greeting: string}",
        "Customer: {customer_first_name} {customer_last_name} ({customer_title}) at {customer_company}. Deal: {deal_name}, value: ${deal_amount}. Renewal date: {renewal_date}. Stage: {deal_stage}. Lifecycle: {lifecycle_stage}.",
        [
          {
            field: "email_subject_90d",
            as: "email_subject_90d",
            type: "string",
          },
          { field: "email_body_90d", as: "email_body_90d", type: "string" },
          {
            field: "email_subject_60d",
            as: "email_subject_60d",
            type: "string",
          },
          { field: "email_body_60d", as: "email_body_60d", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "personalized_greeting",
            as: "personalized_greeting",
            type: "string",
          },
        ],
      ),

      // 5. Touch 1 (90-day) email
      emailNode(
        "5",
        "Send 90-day renewal email (Touch 1)",
        1000,
        "{customer_email}",
        "{email_subject_90d}",
        "{email_body_90d}",
      ),

      // 6. Delay 30 days
      n("6", "delayAction", "Wait 30 days before next touch", 1240, {
        delayDetails: { duration: 30, unit: "days" },
      }),

      // 7. Touch 2 (60-day) email
      emailNode(
        "7",
        "Send 60-day renewal email (Touch 2)",
        1480,
        "{customer_email}",
        "{email_subject_60d}",
        "{email_body_60d}",
      ),

      // 8. Engagement analytics Sheets
      sheetsNode(
        "8",
        "Log renewal engagement analytics (Sheets)",
        1720,
        "Renewal_Analytics",
        '[["{customer_email}","{customer_first_name} {customer_last_name}","{customer_company}","{deal_name}","{deal_amount}","{renewal_date}","{deal_stage}","{value_proposition}","touch_1_sent","touch_2_sent","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1412. REPORT FORMATTER BOT (7 nodes)
  // ===================================================================

  "Report Formatter Bot": {
    summary:
      "Receives report submissions via webhook, analyzes content structure with AI, matches to the best template in code, applies AI-powered formatting and layout, generates the final document in code, evaluates if review is needed, and distributes the formatted report via email.",
    nodes: [
      trigger("Webhook: Report submitted for formatting", "webhook_app", {
        source: "reporting_system",
        event: "report.submitted",
        howItStarts:
          "Fires when a raw report is submitted for formatting — accepts unstructured text, CSV data, or JSON report content that needs professional formatting",
        setup:
          "Configure webhook from your reporting platform or form. Set up OpenAI API key for content analysis and formatting. Configure email for report distribution. Define your report template library.",
        inputFields: [],
      }),

      // 2. AI content analyzer
      aiNode(
        "2",
        "AI: Analyze report content",
        280,
        "You are a report content analyst. Examine the raw webhook payload to extract the report title, author, author email, report type, raw content, data sections, intended audience, and any formatting preferences. Identify the content structure: narrative, data-heavy, mixed, or presentation-ready. Respond JSON: {report_title: string, author_name: string, author_email: string, report_type: string, raw_content: string, content_structure: string, data_sections_count: number, intended_audience: string, formatting_preference: string, word_count: number}",
        "Raw report submission payload: {webhook_payload}",
        [
          { field: "report_title", as: "report_title", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "report_type", as: "report_type", type: "string" },
          { field: "raw_content", as: "raw_content", type: "string" },
          {
            field: "content_structure",
            as: "content_structure",
            type: "string",
          },
          {
            field: "data_sections_count",
            as: "data_sections_count",
            type: "number",
          },
          {
            field: "intended_audience",
            as: "intended_audience",
            type: "string",
          },
          {
            field: "formatting_preference",
            as: "formatting_preference",
            type: "string",
          },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 3. Template matcher runCode
      n("3", "runCode", "Match content to report template", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match report type and structure to the best template
const reportType = (vars.report_type || "general").toLowerCase();
const structure = (vars.content_structure || "narrative").toLowerCase();
const audience = (vars.intended_audience || "internal").toLowerCase();
const templates = {
  executive: { name: "Executive Summary", layout: "header-kpi-narrative-recommendations", font: "Georgia", colors: "navy-gold" },
  technical: { name: "Technical Report", layout: "header-methodology-findings-data-appendix", font: "Consolas", colors: "slate-blue" },
  financial: { name: "Financial Report", layout: "header-summary-tables-charts-footnotes", font: "Arial", colors: "dark-green" },
  quarterly: { name: "Quarterly Review", layout: "header-metrics-trends-forecast-actions", font: "Helvetica", colors: "corporate-blue" },
  general: { name: "Standard Report", layout: "header-sections-conclusion", font: "Calibri", colors: "neutral-gray" },
};
let match = templates.general;
if (reportType.includes("exec") || audience.includes("exec") || audience.includes("board")) match = templates.executive;
else if (reportType.includes("tech") || reportType.includes("engineering")) match = templates.technical;
else if (reportType.includes("financ") || reportType.includes("budget")) match = templates.financial;
else if (reportType.includes("quarter") || reportType.includes("review")) match = templates.quarterly;
return {
  template_name: match.name,
  template_layout: match.layout,
  template_font: match.font,
  template_colors: match.colors,
  match_confidence: reportType.includes(match.name.toLowerCase().split(" ")[0]) ? "HIGH" : "MEDIUM",
};`,
          outputs: [
            { name: "template_name", type: "string" },
            { name: "template_layout", type: "string" },
            { name: "template_font", type: "string" },
            { name: "template_colors", type: "string" },
            { name: "match_confidence", type: "string" },
          ],
        },
      }),

      // 4. AI formatting engine
      aiNode(
        "4",
        "AI: Format report content",
        760,
        "You are a professional report formatter. Take the raw report content and format it according to the specified template layout. Add proper headings, section breaks, bullet points, executive summary, and conclusions. Ensure consistent tone matching the intended audience. Apply the template structure faithfully. Respond JSON: {formatted_html: string, executive_summary: string, section_headings: string, key_findings: string, recommendations: string, formatting_notes: string}",
        "Report: {report_title} by {author_name}. Template: {template_name} (layout: {template_layout}). Audience: {intended_audience}. Preference: {formatting_preference}. Structure: {content_structure}. Raw content: {raw_content}.",
        [
          { field: "formatted_html", as: "formatted_html", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "section_headings", as: "section_headings", type: "string" },
          { field: "key_findings", as: "key_findings", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "formatting_notes", as: "formatting_notes", type: "string" },
        ],
      ),

      // 5. Document generator runCode
      n("5", "runCode", "Generate final formatted document", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Wrap formatted content in a complete document structure
const title = vars.report_title || "Untitled Report";
const font = vars.template_font || "Calibri";
const colors = vars.template_colors || "neutral-gray";
const colorMap = {
  "navy-gold": { primary: "#1a237e", accent: "#ffd700" },
  "slate-blue": { primary: "#37474f", accent: "#42a5f5" },
  "dark-green": { primary: "#1b5e20", accent: "#66bb6a" },
  "corporate-blue": { primary: "#0d47a1", accent: "#1976d2" },
  "neutral-gray": { primary: "#424242", accent: "#757575" },
};
const palette = colorMap[colors] || colorMap["neutral-gray"];
const wordCount = parseInt(vars.word_count || "500", 10);
const requiresReview = wordCount > 2000 || vars.match_confidence === "MEDIUM" ? "yes" : "no";
const documentHtml = "<html><head><style>body{font-family:" + font + ";color:" + palette.primary + ";}h1{color:" + palette.accent + ";}</style></head><body>" + vars.formatted_html + "</body></html>";
const docId = "RPT-" + Date.now().toString(36).toUpperCase();
return {
  document_html: documentHtml,
  document_id: docId,
  requires_review: requiresReview,
  generation_timestamp: new Date().toISOString(),
  primary_color: palette.primary,
};`,
          outputs: [
            { name: "document_html", type: "string" },
            { name: "document_id", type: "string" },
            { name: "requires_review", type: "string" },
            { name: "generation_timestamp", type: "string" },
            { name: "primary_color", type: "string" },
          ],
        },
      }),

      // 6. Review revision condition
      n("6", "condition", "Check: Does report require review?", 1240, {
        conditionData: [
          { variable: "requires_review", operator: "equals", value: "yes" },
        ],
      }),

      // 7. Distribution archive email
      emailNode(
        "7",
        "Distribute formatted report via email",
        1480,
        "{author_email}",
        "Formatted Report Ready: {report_title} ({document_id})",
        "<h2>Your Formatted Report is Ready</h2><p>Hi {author_name},</p><p>Your report <strong>{report_title}</strong> has been formatted using the <strong>{template_name}</strong> template.</p><p><strong>Document ID:</strong> {document_id}</p><p><strong>Template:</strong> {template_name} ({template_layout})</p><p><strong>Match Confidence:</strong> {match_confidence}</p><p><strong>Review Required:</strong> {requires_review}</p><hr/><h3>Executive Summary</h3><p>{executive_summary}</p><h3>Key Findings</h3><p>{key_findings}</p><h3>Formatting Notes</h3><p>{formatting_notes}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1413. REPORT SCHEDULING AND DISTRIBUTION (8 nodes)
  // ===================================================================

  "Report Scheduling and Distribution": {
    summary:
      "Runs on a configurable schedule to aggregate data from multiple sources in code, compiles a comprehensive report with AI, renders the report into distributable format in code, routes recipients by team in code, delivers by email and Slack, and confirms delivery in Sheets.",
    nodes: [
      trigger("Schedule: Configured report interval", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Fires on a configured schedule (weekly Monday, bi-weekly, monthly) to aggregate data from multiple sources, compile a comprehensive report, and distribute to stakeholders",
        setup:
          "Configure data source API keys. Set OpenAI API key for AI report compilation. Set up email distribution lists. Configure Slack webhook. Set Google Sheets for delivery tracking. Define report frequency and recipients.",
        inputFields: [],
      }),

      // 2. Multi-source data aggregator runCode
      n("2", "runCode", "Aggregate data from multiple sources", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate and normalize data from multiple sources
const reportPeriod = new Date();
reportPeriod.setDate(reportPeriod.getDate() - 7);
const periodStart = reportPeriod.toISOString().split("T")[0];
const periodEnd = new Date().toISOString().split("T")[0];
const reportId = "WR-" + Date.now().toString(36).toUpperCase();
// Simulated aggregated metrics — in production these come from API responses
return {
  report_id: reportId,
  period_start: periodStart,
  period_end: periodEnd,
  report_period_label: periodStart + " to " + periodEnd,
  data_sources_queried: "CRM, Analytics, Finance, Support",
  aggregation_timestamp: new Date().toISOString(),
  total_sources: 4,
};`,
          outputs: [
            { name: "report_id", type: "string" },
            { name: "period_start", type: "string" },
            { name: "period_end", type: "string" },
            { name: "report_period_label", type: "string" },
            { name: "data_sources_queried", type: "string" },
            { name: "aggregation_timestamp", type: "string" },
            { name: "total_sources", type: "number" },
          ],
        },
      }),

      // 3. AI report compiler
      aiNode(
        "3",
        "AI: Compile comprehensive report",
        520,
        "You are a business intelligence analyst. Compile a comprehensive weekly report covering key performance indicators, trends, notable achievements, and areas requiring attention. Structure the report with an executive overview, departmental highlights, key metrics, risk areas, and recommended actions. Write for a C-level audience. Respond JSON: {executive_overview: string, departmental_highlights: string, key_metrics_summary: string, risk_areas: string, recommended_actions: string, report_title: string, overall_health: string}",
        "Report ID: {report_id}. Period: {report_period_label}. Data sources: {data_sources_queried}. Sources queried: {total_sources}. Compile a professional weekly business report.",
        [
          {
            field: "executive_overview",
            as: "executive_overview",
            type: "string",
          },
          {
            field: "departmental_highlights",
            as: "departmental_highlights",
            type: "string",
          },
          {
            field: "key_metrics_summary",
            as: "key_metrics_summary",
            type: "string",
          },
          { field: "risk_areas", as: "risk_areas", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          { field: "report_title", as: "report_title", type: "string" },
          { field: "overall_health", as: "overall_health", type: "string" },
        ],
      ),

      // 4. Report renderer runCode
      n("4", "runCode", "Render report into distributable format", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Render the compiled report into HTML format
const reportHtml = "<html><body>" +
  "<h1>" + vars.report_title + "</h1>" +
  "<p><em>Report ID: " + vars.report_id + " | Period: " + vars.report_period_label + "</em></p>" +
  "<p><strong>Overall Health:</strong> " + vars.overall_health + "</p>" +
  "<hr/><h2>Executive Overview</h2><p>" + vars.executive_overview + "</p>" +
  "<h2>Departmental Highlights</h2><p>" + vars.departmental_highlights + "</p>" +
  "<h2>Key Metrics</h2><p>" + vars.key_metrics_summary + "</p>" +
  "<h2>Risk Areas</h2><p>" + vars.risk_areas + "</p>" +
  "<h2>Recommended Actions</h2><p>" + vars.recommended_actions + "</p>" +
  "</body></html>";
const slackSummary = "*" + vars.report_title + "*\\nPeriod: " + vars.report_period_label + "\\nHealth: " + vars.overall_health + "\\n\\n" + vars.executive_overview;
return {
  report_html: reportHtml,
  slack_summary: slackSummary,
  render_timestamp: new Date().toISOString(),
};`,
          outputs: [
            { name: "report_html", type: "string" },
            { name: "slack_summary", type: "string" },
            { name: "render_timestamp", type: "string" },
          ],
        },
      }),

      // 5. Recipient routing engine runCode
      n("5", "runCode", "Route to recipients by team", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine distribution list based on report type
const health = (vars.overall_health || "").toUpperCase();
const includeExec = health === "RED" || health === "AT RISK" || true;
const channels = ["#weekly-reports"];
if (health === "RED") channels.push("#leadership-alerts");
return {
  email_recipients: "{report_distribution_email}",
  slack_channels: channels.join(", "),
  distribution_scope: includeExec ? "ALL_STAKEHOLDERS" : "DEPARTMENT_LEADS",
  total_recipients: includeExec ? "executive + department leads" : "department leads",
};`,
          outputs: [
            { name: "email_recipients", type: "string" },
            { name: "slack_channels", type: "string" },
            { name: "distribution_scope", type: "string" },
            { name: "total_recipients", type: "string" },
          ],
        },
      }),

      // 6. Email delivery
      emailNode(
        "6",
        "Deliver report via email",
        1240,
        "{email_recipients}",
        "{report_title} — {report_period_label}",
        "{report_html}",
      ),

      // 7. Slack delivery
      slackNode(
        "7",
        "Deliver report summary to Slack",
        1240,
        ":bar_chart: *Weekly Report Published*\n*{report_title}*\nPeriod: {report_period_label} | Health: {overall_health}\n\n{executive_overview}\n\n*Key Metrics:* {key_metrics_summary}\n*Risk Areas:* {risk_areas}\n\nFull report delivered to: {total_recipients}",
        380,
      ),

      // 8. Delivery confirmation Sheets
      sheetsNode(
        "8",
        "Log delivery confirmation (Sheets)",
        1480,
        "Report_Delivery",
        '[["{report_id}","{report_title}","{report_period_label}","{overall_health}","{distribution_scope}","{total_recipients}","{render_timestamp}","delivered","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1414. RESOURCE ALLOCATION OPTIMIZER (8 nodes)
  // ===================================================================

  "Resource Allocation Optimizer": {
    summary:
      "Runs weekly to fetch current resource allocation from Asana, forecasts demand in code, generates AI optimization recommendations, models allocation scenarios in code, presents recommendations to Slack, routes through an approval condition, and tracks effectiveness in Sheets.",
    nodes: [
      trigger("Schedule: Weekly resource optimization", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Fires every Monday at 9 AM to analyze current resource allocation across teams and projects, forecast demand, and generate optimization recommendations",
        setup:
          "Configure Asana API token for project and task data. Set OpenAI API key for AI optimization. Set up Slack webhook for recommendations. Configure Google Sheets for effectiveness tracking. Define team capacity data.",
        inputFields: [],
      }),

      // 2. Current allocation fetch — Asana API
      n("2", "hitApi", "Fetch current resource allocation from Asana", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/workspaces/{workspace_gid}/tasks?opt_fields=name,assignee.name,due_on,completed,custom_fields&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].name",
              customName: "first_task_name",
              type: "string",
            },
            {
              name: "data[0].assignee.name",
              customName: "first_assignee",
              type: "string",
            },
            {
              name: "data[0].due_on",
              customName: "first_due_date",
              type: "string",
            },
            {
              name: "data.length",
              customName: "total_active_tasks",
              type: "number",
            },
          ],
        },
      }),

      // 3. Demand forecast runCode
      n("3", "runCode", "Forecast resource demand", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Forecast demand based on current task load
const activeTasks = parseInt(vars.total_active_tasks || "0", 10);
const avgTasksPerPerson = 8;
const currentTeamSize = 10;
const currentCapacity = currentTeamSize * avgTasksPerPerson;
const utilization = Math.round((activeTasks / currentCapacity) * 100);
const demandGrowthRate = 0.05; // 5% weekly growth estimate
const forecastedTasks2Weeks = Math.round(activeTasks * (1 + demandGrowthRate * 2));
const forecastedTasks4Weeks = Math.round(activeTasks * (1 + demandGrowthRate * 4));
const capacityGap2Weeks = forecastedTasks2Weeks - currentCapacity;
const capacityGap4Weeks = forecastedTasks4Weeks - currentCapacity;
const allocationStatus = utilization >= 90 ? "OVERLOADED" : utilization >= 70 ? "OPTIMAL" : "UNDERUTILIZED";
return {
  current_utilization: utilization,
  allocation_status: allocationStatus,
  active_tasks: activeTasks,
  current_capacity: currentCapacity,
  forecasted_tasks_2w: forecastedTasks2Weeks,
  forecasted_tasks_4w: forecastedTasks4Weeks,
  capacity_gap_2w: capacityGap2Weeks,
  capacity_gap_4w: capacityGap4Weeks,
  forecast_date: new Date().toISOString().split("T")[0],
};`,
          outputs: [
            { name: "current_utilization", type: "number" },
            { name: "allocation_status", type: "string" },
            { name: "active_tasks", type: "number" },
            { name: "current_capacity", type: "number" },
            { name: "forecasted_tasks_2w", type: "number" },
            { name: "forecasted_tasks_4w", type: "number" },
            { name: "capacity_gap_2w", type: "number" },
            { name: "capacity_gap_4w", type: "number" },
            { name: "forecast_date", type: "string" },
          ],
        },
      }),

      // 4. AI optimization engine
      aiNode(
        "4",
        "AI: Generate allocation recommendations",
        760,
        "You are a resource management strategist. Analyze the current allocation metrics and demand forecast to generate specific optimization recommendations. Consider team rebalancing, task reprioritization, hiring needs, and outsourcing options. Provide 3 actionable scenarios with pros and cons. Respond JSON: {scenario_1_name: string, scenario_1_description: string, scenario_1_impact: string, scenario_2_name: string, scenario_2_description: string, scenario_2_impact: string, scenario_3_name: string, scenario_3_description: string, scenario_3_impact: string, recommended_scenario: string, risk_assessment: string, implementation_timeline: string}",
        "Utilization: {current_utilization}%. Status: {allocation_status}. Active tasks: {active_tasks}/{current_capacity} capacity. 2-week forecast: {forecasted_tasks_2w} tasks (gap: {capacity_gap_2w}). 4-week forecast: {forecasted_tasks_4w} tasks (gap: {capacity_gap_4w}). Team size: 10. Avg tasks/person: 8.",
        [
          { field: "scenario_1_name", as: "scenario_1_name", type: "string" },
          {
            field: "scenario_1_description",
            as: "scenario_1_description",
            type: "string",
          },
          {
            field: "scenario_1_impact",
            as: "scenario_1_impact",
            type: "string",
          },
          { field: "scenario_2_name", as: "scenario_2_name", type: "string" },
          {
            field: "scenario_2_description",
            as: "scenario_2_description",
            type: "string",
          },
          {
            field: "scenario_2_impact",
            as: "scenario_2_impact",
            type: "string",
          },
          { field: "scenario_3_name", as: "scenario_3_name", type: "string" },
          {
            field: "scenario_3_description",
            as: "scenario_3_description",
            type: "string",
          },
          {
            field: "scenario_3_impact",
            as: "scenario_3_impact",
            type: "string",
          },
          {
            field: "recommended_scenario",
            as: "recommended_scenario",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "implementation_timeline",
            as: "implementation_timeline",
            type: "string",
          },
        ],
      ),

      // 5. Scenario modeler runCode
      n("5", "runCode", "Model allocation scenarios", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Model financial and operational impact of recommended scenario
const utilization = parseInt(vars.current_utilization || "80", 10);
const gap2w = parseInt(vars.capacity_gap_2w || "0", 10);
const costPerTaskHour = 75;
const overtimeCost = gap2w > 0 ? gap2w * costPerTaskHour * 2 : 0;
const hiringCost = gap2w > 10 ? Math.ceil(gap2w / 8) * 85000 : 0;
const contractorCost = gap2w > 0 ? gap2w * costPerTaskHour * 1.5 : 0;
const recommendedBudget = Math.min(overtimeCost, contractorCost);
const projectedUtilization = Math.min(95, Math.max(60, utilization - (gap2w > 0 ? 0 : 10)));
return {
  overtime_cost_estimate: "$" + overtimeCost.toLocaleString(),
  hiring_cost_estimate: "$" + hiringCost.toLocaleString(),
  contractor_cost_estimate: "$" + contractorCost.toLocaleString(),
  recommended_budget: "$" + recommendedBudget.toLocaleString(),
  projected_utilization: projectedUtilization + "%",
  model_date: new Date().toISOString(),
};`,
          outputs: [
            { name: "overtime_cost_estimate", type: "string" },
            { name: "hiring_cost_estimate", type: "string" },
            { name: "contractor_cost_estimate", type: "string" },
            { name: "recommended_budget", type: "string" },
            { name: "projected_utilization", type: "string" },
            { name: "model_date", type: "string" },
          ],
        },
      }),

      // 6. Recommendation presenter Slack
      slackNode(
        "6",
        "Present optimization recommendations (Slack)",
        1240,
        ":chart_with_upwards_trend: *Weekly Resource Allocation Report*\n*Status:* {allocation_status} ({current_utilization}% utilization)\n*Active Tasks:* {active_tasks} / {current_capacity} capacity\n*2-Week Forecast:* {forecasted_tasks_2w} tasks (gap: {capacity_gap_2w})\n\n*Recommended:* {recommended_scenario}\n1. *{scenario_1_name}:* {scenario_1_description} — {scenario_1_impact}\n2. *{scenario_2_name}:* {scenario_2_description} — {scenario_2_impact}\n3. *{scenario_3_name}:* {scenario_3_description} — {scenario_3_impact}\n\n*Budget:* Overtime: {overtime_cost_estimate} | Contractors: {contractor_cost_estimate} | Hiring: {hiring_cost_estimate}\n*Risk:* {risk_assessment}\n*Timeline:* {implementation_timeline}",
      ),

      // 7. Approval implementation condition
      n("7", "condition", "Check: Is utilization above 85%?", 1480, {
        conditionData: [
          {
            variable: "current_utilization",
            operator: "greater_than",
            value: "85",
          },
        ],
      }),

      // 8. Effectiveness tracker Sheets
      sheetsNode(
        "8",
        "Track allocation effectiveness (Sheets)",
        1720,
        "Resource_Allocation",
        '[["{forecast_date}","{allocation_status}","{current_utilization}","{active_tasks}","{current_capacity}","{forecasted_tasks_2w}","{capacity_gap_2w}","{recommended_scenario}","{recommended_budget}","{projected_utilization}","{risk_assessment}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1415. RESOURCE REQUEST BOT (7 nodes)
  // ===================================================================

  "Resource Request Bot": {
    summary:
      "Receives resource requests via webhook, extracts request details with AI, validates the request with AI analysis, checks resource availability in code, routes through an approval condition based on availability, executes allocation via API, and logs the request to Sheets.",
    nodes: [
      trigger("Webhook: Resource request submitted", "webhook_app", {
        source: "resource_portal",
        event: "resource.requested",
        howItStarts:
          "Fires when a team member submits a resource request (equipment, software licenses, cloud resources, meeting rooms, or personnel) through the resource portal or form",
        setup:
          "Configure webhook from your resource management portal or form. Set up OpenAI API key for request analysis. Configure your resource inventory API. Set Google Sheets for request tracking.",
        inputFields: [],
      }),

      // 2. AI extract request data
      aiNode(
        "2",
        "AI: Extract resource request details",
        280,
        "You are a resource management coordinator. Extract the request details from the raw webhook payload regardless of format. Find the requester name, requester email, department, resource type (equipment, software, cloud, room, personnel), specific resource name, quantity, justification, urgency level, desired start date, and duration. Respond JSON: {requester_name: string, requester_email: string, department: string, resource_type: string, resource_name: string, quantity: number, justification: string, urgency: string, start_date: string, duration: string, estimated_cost: string}",
        "Raw resource request payload: {webhook_payload}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "resource_type", as: "resource_type", type: "string" },
          { field: "resource_name", as: "resource_name", type: "string" },
          { field: "quantity", as: "quantity", type: "number" },
          { field: "justification", as: "justification", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "duration", as: "duration", type: "string" },
          { field: "estimated_cost", as: "estimated_cost", type: "string" },
        ],
      ),

      // 3. AI request validator
      aiNode(
        "3",
        "AI: Validate request completeness",
        520,
        "You are a resource request reviewer. Evaluate the completeness and validity of this resource request. Check if the justification is adequate, the quantity is reasonable for the department size, the urgency matches the request type, and the cost estimate is within typical ranges. Flag any concerns. Respond JSON: {validation_status: string, completeness_score: number, justification_adequate: boolean, quantity_reasonable: boolean, concerns: string, recommendation: string, approval_confidence: string}",
        "Requester: {requester_name} ({department}). Resource: {resource_name} ({resource_type}), qty: {quantity}. Urgency: {urgency}. Start: {start_date}. Duration: {duration}. Cost: {estimated_cost}. Justification: {justification}.",
        [
          {
            field: "validation_status",
            as: "validation_status",
            type: "string",
          },
          {
            field: "completeness_score",
            as: "completeness_score",
            type: "number",
          },
          {
            field: "justification_adequate",
            as: "justification_adequate",
            type: "boolean",
          },
          {
            field: "quantity_reasonable",
            as: "quantity_reasonable",
            type: "boolean",
          },
          { field: "concerns", as: "concerns", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
          {
            field: "approval_confidence",
            as: "approval_confidence",
            type: "string",
          },
        ],
      ),

      // 4. Availability checker runCode
      n("4", "runCode", "Check resource availability", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check availability and calculate allocation feasibility
const qty = parseInt(vars.quantity || "1", 10);
const resourceType = (vars.resource_type || "").toLowerCase();
const validationStatus = (vars.validation_status || "").toUpperCase();
const completenessScore = parseInt(vars.completeness_score || "0", 10);
const justificationOk = vars.justification_adequate === "true" || vars.justification_adequate === true;
// Determine if auto-approval is possible
const lowRisk = qty <= 5 && justificationOk && completenessScore >= 70;
const autoApprovable = lowRisk && validationStatus !== "REJECTED";
const requestId = "RR-" + Date.now().toString(36).toUpperCase();
const availabilityStatus = autoApprovable ? "AVAILABLE" : "PENDING_REVIEW";
return {
  request_id: requestId,
  availability_status: availabilityStatus,
  auto_approvable: autoApprovable,
  fulfillment_estimate: qty <= 3 ? "1-2 business days" : "3-5 business days",
  allocation_ready: autoApprovable ? "yes" : "no",
};`,
          outputs: [
            { name: "request_id", type: "string" },
            { name: "availability_status", type: "string" },
            { name: "auto_approvable", type: "boolean" },
            { name: "fulfillment_estimate", type: "string" },
            { name: "allocation_ready", type: "string" },
          ],
        },
      }),

      // 5. Approval router condition
      n("5", "condition", "Check: Is auto-approval eligible?", 1000, {
        conditionData: [
          { variable: "allocation_ready", operator: "equals", value: "yes" },
        ],
      }),

      // 6. Allocation executor API (True branch)
      n("6", "hitApi", "Execute resource allocation", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[RESOURCE] {request_id}: {resource_name} x{quantity} for {requester_name}","notes":"Type: {resource_type}\\nDepartment: {department}\\nQuantity: {quantity}\\nUrgency: {urgency}\\nStart: {start_date}\\nDuration: {duration}\\nCost: {estimated_cost}\\nStatus: {availability_status}\\nFulfillment: {fulfillment_estimate}\\n\\nJustification: {justification}\\nValidation: {validation_status} (Score: {completeness_score})\\nRecommendation: {recommendation}","projects":["{asana_resources_project_id}"]}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "allocation_task_id",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "allocation_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Request tracker Sheets
      sheetsNode(
        "7",
        "Log resource request (Sheets)",
        1480,
        "Resource_Requests",
        '[["{request_id}","{requester_name}","{requester_email}","{department}","{resource_type}","{resource_name}","{quantity}","{urgency}","{start_date}","{duration}","{estimated_cost}","{validation_status}","{completeness_score}","{availability_status}","{allocation_ready}","{fulfillment_estimate}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1416. RISK ASSESSMENT BOT (8 nodes)
  // ===================================================================

  "Risk Assessment Bot": {
    summary:
      "Receives risk assessment requests via webhook, extracts context with AI, gathers related issue data from Jira, identifies specific risks with AI analysis, scores risks using a quantitative matrix in code, generates mitigation strategies with AI, delivers the risk report by email, and updates the risk register in Sheets.",
    nodes: [
      trigger("Webhook: Risk assessment requested", "webhook_app", {
        source: "risk_platform",
        event: "risk.assessment_requested",
        howItStarts:
          "Fires when a risk assessment is requested for a project, initiative, vendor, or operational change — typically from a GRC platform, project management tool, or internal form",
        setup:
          "Configure webhook from your risk management or GRC platform. Set OpenAI API key for risk identification and mitigation. Configure Jira API for related issues. Set email for report delivery. Set Google Sheets for risk register.",
        inputFields: [],
      }),

      // 2. AI extract context
      aiNode(
        "2",
        "AI: Extract risk assessment context",
        280,
        "You are a risk management analyst. Extract the assessment context from the raw webhook payload regardless of format. Find the assessment subject (project name, vendor, initiative), requester name, requester email, risk domain (operational, financial, security, compliance, strategic, technical), scope description, existing controls, and any known risk factors. Respond JSON: {subject_name: string, requester_name: string, requester_email: string, risk_domain: string, scope_description: string, existing_controls: string, known_risk_factors: string, assessment_priority: string, stakeholders: string, project_key: string}",
        "Raw risk assessment request: {webhook_payload}",
        [
          { field: "subject_name", as: "subject_name", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "risk_domain", as: "risk_domain", type: "string" },
          {
            field: "scope_description",
            as: "scope_description",
            type: "string",
          },
          {
            field: "existing_controls",
            as: "existing_controls",
            type: "string",
          },
          {
            field: "known_risk_factors",
            as: "known_risk_factors",
            type: "string",
          },
          {
            field: "assessment_priority",
            as: "assessment_priority",
            type: "string",
          },
          { field: "stakeholders", as: "stakeholders", type: "string" },
          { field: "project_key", as: "project_key", type: "string" },
        ],
      ),

      // 3. Context data gatherer — Jira API
      n("3", "hitApi", "Fetch related Jira issues for context", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=project={project_key}+AND+type=Bug+AND+status!=Done&maxResults=20",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "open_issues_count", type: "number" },
            {
              name: "issues[0].fields.summary",
              customName: "top_issue_summary",
              type: "string",
            },
            {
              name: "issues[0].fields.priority.name",
              customName: "top_issue_priority",
              type: "string",
            },
            {
              name: "issues[0].fields.status.name",
              customName: "top_issue_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI risk identification
      aiNode(
        "4",
        "AI: Identify and categorize risks",
        760,
        "You are a senior risk assessor. Based on the subject context, known risk factors, existing controls, and related issues data, identify the top 5 specific risks. For each risk, provide a description, likelihood (1-5), impact (1-5), and category (strategic, operational, financial, compliance, reputational, technical). Respond JSON: {risk_1_name: string, risk_1_likelihood: number, risk_1_impact: number, risk_1_category: string, risk_2_name: string, risk_2_likelihood: number, risk_2_impact: number, risk_2_category: string, risk_3_name: string, risk_3_likelihood: number, risk_3_impact: number, risk_3_category: string, overall_risk_profile: string, risk_trend: string}",
        "Subject: {subject_name}. Domain: {risk_domain}. Scope: {scope_description}. Known factors: {known_risk_factors}. Existing controls: {existing_controls}. Open issues: {open_issues_count}. Top issue: {top_issue_summary} ({top_issue_priority}). Priority: {assessment_priority}.",
        [
          { field: "risk_1_name", as: "risk_1_name", type: "string" },
          {
            field: "risk_1_likelihood",
            as: "risk_1_likelihood",
            type: "number",
          },
          { field: "risk_1_impact", as: "risk_1_impact", type: "number" },
          { field: "risk_1_category", as: "risk_1_category", type: "string" },
          { field: "risk_2_name", as: "risk_2_name", type: "string" },
          {
            field: "risk_2_likelihood",
            as: "risk_2_likelihood",
            type: "number",
          },
          { field: "risk_2_impact", as: "risk_2_impact", type: "number" },
          { field: "risk_2_category", as: "risk_2_category", type: "string" },
          { field: "risk_3_name", as: "risk_3_name", type: "string" },
          {
            field: "risk_3_likelihood",
            as: "risk_3_likelihood",
            type: "number",
          },
          { field: "risk_3_impact", as: "risk_3_impact", type: "number" },
          { field: "risk_3_category", as: "risk_3_category", type: "string" },
          {
            field: "overall_risk_profile",
            as: "overall_risk_profile",
            type: "string",
          },
          { field: "risk_trend", as: "risk_trend", type: "string" },
        ],
      ),

      // 5. Risk scoring matrix runCode
      n("5", "runCode", "Calculate risk scores with scoring matrix", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate risk scores using likelihood x impact matrix
const risks = [
  { name: vars.risk_1_name, likelihood: parseInt(vars.risk_1_likelihood || "3", 10), impact: parseInt(vars.risk_1_impact || "3", 10), category: vars.risk_1_category },
  { name: vars.risk_2_name, likelihood: parseInt(vars.risk_2_likelihood || "2", 10), impact: parseInt(vars.risk_2_impact || "2", 10), category: vars.risk_2_category },
  { name: vars.risk_3_name, likelihood: parseInt(vars.risk_3_likelihood || "2", 10), impact: parseInt(vars.risk_3_impact || "2", 10), category: vars.risk_3_category },
];
risks.forEach(r => {
  r.score = r.likelihood * r.impact;
  r.level = r.score >= 15 ? "CRITICAL" : r.score >= 10 ? "HIGH" : r.score >= 5 ? "MEDIUM" : "LOW";
});
risks.sort((a, b) => b.score - a.score);
const avgScore = Math.round(risks.reduce((sum, r) => sum + r.score, 0) / risks.length);
const compositeLevel = avgScore >= 15 ? "CRITICAL" : avgScore >= 10 ? "HIGH" : avgScore >= 5 ? "MEDIUM" : "LOW";
const assessmentId = "RA-" + Date.now().toString(36).toUpperCase();
const riskMatrix = risks.map(r => r.name + " [L:" + r.likelihood + " x I:" + r.impact + " = " + r.score + " " + r.level + "]").join("; ");
return {
  assessment_id: assessmentId,
  risk_matrix_summary: riskMatrix,
  top_risk_name: risks[0].name,
  top_risk_score: risks[0].score,
  top_risk_level: risks[0].level,
  composite_risk_score: avgScore,
  composite_risk_level: compositeLevel,
  assessment_date: new Date().toISOString(),
};`,
          outputs: [
            { name: "assessment_id", type: "string" },
            { name: "risk_matrix_summary", type: "string" },
            { name: "top_risk_name", type: "string" },
            { name: "top_risk_score", type: "number" },
            { name: "top_risk_level", type: "string" },
            { name: "composite_risk_score", type: "number" },
            { name: "composite_risk_level", type: "string" },
            { name: "assessment_date", type: "string" },
          ],
        },
      }),

      // 6. Mitigation strategy generator AI
      aiNode(
        "6",
        "AI: Generate mitigation strategies",
        1240,
        "You are a risk mitigation specialist. For each identified risk, generate specific, actionable mitigation strategies. Include preventive controls, detective controls, and corrective actions. Prioritize mitigations by effectiveness and cost. Respond JSON: {top_risk_mitigation: string, risk_2_mitigation: string, risk_3_mitigation: string, preventive_controls: string, monitoring_plan: string, contingency_plan: string, residual_risk_estimate: string}",
        "Subject: {subject_name}. Risk matrix: {risk_matrix_summary}. Composite level: {composite_risk_level}. Top risk: {top_risk_name} (score: {top_risk_score}). Existing controls: {existing_controls}. Open issues: {open_issues_count}. Domain: {risk_domain}.",
        [
          {
            field: "top_risk_mitigation",
            as: "top_risk_mitigation",
            type: "string",
          },
          {
            field: "risk_2_mitigation",
            as: "risk_2_mitigation",
            type: "string",
          },
          {
            field: "risk_3_mitigation",
            as: "risk_3_mitigation",
            type: "string",
          },
          {
            field: "preventive_controls",
            as: "preventive_controls",
            type: "string",
          },
          { field: "monitoring_plan", as: "monitoring_plan", type: "string" },
          { field: "contingency_plan", as: "contingency_plan", type: "string" },
          {
            field: "residual_risk_estimate",
            as: "residual_risk_estimate",
            type: "string",
          },
        ],
      ),

      // 7. Risk report email
      emailNode(
        "7",
        "Deliver risk assessment report",
        1480,
        "{requester_email}",
        "Risk Assessment Complete: {subject_name} — {composite_risk_level} ({assessment_id})",
        "<h2>Risk Assessment Report — {assessment_id}</h2><p><strong>Subject:</strong> {subject_name}</p><p><strong>Domain:</strong> {risk_domain} | <strong>Priority:</strong> {assessment_priority}</p><p><strong>Composite Risk Level:</strong> {composite_risk_level} (Score: {composite_risk_score}/25)</p><p><strong>Trend:</strong> {risk_trend}</p><hr/><h3>Risk Matrix</h3><p>{risk_matrix_summary}</p><h3>Top Risk Mitigation</h3><p><strong>{top_risk_name}:</strong> {top_risk_mitigation}</p><h3>Preventive Controls</h3><p>{preventive_controls}</p><h3>Monitoring Plan</h3><p>{monitoring_plan}</p><h3>Contingency Plan</h3><p>{contingency_plan}</p><p><strong>Residual Risk:</strong> {residual_risk_estimate}</p>",
      ),

      // 8. Risk register update Sheets
      sheetsNode(
        "8",
        "Update risk register (Sheets)",
        1720,
        "Risk_Register",
        '[["{assessment_id}","{subject_name}","{risk_domain}","{assessment_priority}","{composite_risk_level}","{composite_risk_score}","{top_risk_name}","{top_risk_score}","{risk_matrix_summary}","{residual_risk_estimate}","{requester_name}","{assessment_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1417. RISK REGISTER UPDATE WORKFLOW (7 nodes)
  // ===================================================================

  "Risk Register Update Workflow": {
    summary:
      "Receives risk events via webhook, extracts risk data with AI, fetches the existing risk register from Sheets, re-assesses risk levels with AI, recalculates scores in code, updates the register in Sheets, and notifies the risk team via Slack.",
    nodes: [
      trigger("Webhook: Risk event reported", "webhook_app", {
        source: "risk_monitoring",
        event: "risk.event_detected",
        howItStarts:
          "Fires when a risk event is detected or reported — such as an incident, near-miss, control failure, or environmental change that may affect existing risk entries",
        setup:
          "Configure webhook from your risk monitoring system or incident platform. Set up OpenAI API key for risk re-assessment. Configure Google Sheets with your risk register. Set up Slack webhook for notifications.",
        inputFields: [],
      }),

      // 2. AI extract risk data
      aiNode(
        "2",
        "AI: Extract risk event details",
        280,
        "You are a risk event analyst. Extract the event details from the raw webhook payload regardless of format. Find the event description, event type (incident, near-miss, control_failure, environment_change, audit_finding), affected risk ID or area, severity, date occurred, reporter name, reporter email, and any immediate actions taken. Respond JSON: {event_description: string, event_type: string, affected_risk_area: string, severity: string, date_occurred: string, reporter_name: string, reporter_email: string, immediate_actions: string, affected_controls: string, evidence_reference: string}",
        "Raw risk event payload: {webhook_payload}",
        [
          {
            field: "event_description",
            as: "event_description",
            type: "string",
          },
          { field: "event_type", as: "event_type", type: "string" },
          {
            field: "affected_risk_area",
            as: "affected_risk_area",
            type: "string",
          },
          { field: "severity", as: "event_severity", type: "string" },
          { field: "date_occurred", as: "date_occurred", type: "string" },
          { field: "reporter_name", as: "reporter_name", type: "string" },
          { field: "reporter_email", as: "reporter_email", type: "string" },
          {
            field: "immediate_actions",
            as: "immediate_actions",
            type: "string",
          },
          {
            field: "affected_controls",
            as: "affected_controls",
            type: "string",
          },
          {
            field: "evidence_reference",
            as: "evidence_reference",
            type: "string",
          },
        ],
      ),

      // 3. Risk data fetcher Sheets
      n("3", "hitApi", "Fetch current risk register from Sheets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Risk_Register!A2:L100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values.length",
              customName: "total_registered_risks",
              type: "number",
            },
            {
              name: "values[0][0]",
              customName: "first_risk_id",
              type: "string",
            },
            {
              name: "values[0][1]",
              customName: "first_risk_name",
              type: "string",
            },
            {
              name: "values[0][4]",
              customName: "first_risk_level",
              type: "string",
            },
            {
              name: "values[0][5]",
              customName: "first_risk_score",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI risk re-assessment
      aiNode(
        "4",
        "AI: Re-assess risk levels after event",
        760,
        "You are a risk re-assessment specialist. Based on the new risk event and the existing register data, determine how the event affects current risk levels. Assess whether likelihood, impact, or both have changed. Identify any new risks introduced by this event. Recommend updated risk levels. Respond JSON: {likelihood_change: string, impact_change: string, new_likelihood: number, new_impact: number, risk_level_change: string, new_risks_identified: string, control_effectiveness_change: string, reassessment_rationale: string, recommended_actions: string}",
        "Event: {event_description}. Type: {event_type}. Severity: {event_severity}. Affected area: {affected_risk_area}. Controls affected: {affected_controls}. Immediate actions: {immediate_actions}. Register has {total_registered_risks} risks. Current top risk: {first_risk_name} at level {first_risk_level} (score: {first_risk_score}).",
        [
          {
            field: "likelihood_change",
            as: "likelihood_change",
            type: "string",
          },
          { field: "impact_change", as: "impact_change", type: "string" },
          { field: "new_likelihood", as: "new_likelihood", type: "number" },
          { field: "new_impact", as: "new_impact", type: "number" },
          {
            field: "risk_level_change",
            as: "risk_level_change",
            type: "string",
          },
          {
            field: "new_risks_identified",
            as: "new_risks_identified",
            type: "string",
          },
          {
            field: "control_effectiveness_change",
            as: "control_effectiveness_change",
            type: "string",
          },
          {
            field: "reassessment_rationale",
            as: "reassessment_rationale",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),

      // 5. Score recalculator runCode
      n("5", "runCode", "Recalculate risk scores", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Recalculate risk scores based on AI re-assessment
const newLikelihood = parseInt(vars.new_likelihood || "3", 10);
const newImpact = parseInt(vars.new_impact || "3", 10);
const newScore = newLikelihood * newImpact;
const newLevel = newScore >= 15 ? "CRITICAL" : newScore >= 10 ? "HIGH" : newScore >= 5 ? "MEDIUM" : "LOW";
const previousScore = parseInt(vars.first_risk_score || "0", 10);
const scoreDelta = newScore - previousScore;
const direction = scoreDelta > 0 ? "INCREASED" : scoreDelta < 0 ? "DECREASED" : "UNCHANGED";
const eventId = "RE-" + Date.now().toString(36).toUpperCase();
const updateTimestamp = new Date().toISOString();
return {
  event_id: eventId,
  new_risk_score: newScore,
  new_risk_level: newLevel,
  score_delta: scoreDelta,
  score_direction: direction,
  previous_score: previousScore,
  update_timestamp: updateTimestamp,
  update_summary: "Score " + direction + " from " + previousScore + " to " + newScore + " (" + newLevel + ")",
};`,
          outputs: [
            { name: "event_id", type: "string" },
            { name: "new_risk_score", type: "number" },
            { name: "new_risk_level", type: "string" },
            { name: "score_delta", type: "number" },
            { name: "score_direction", type: "string" },
            { name: "previous_score", type: "number" },
            { name: "update_timestamp", type: "string" },
            { name: "update_summary", type: "string" },
          ],
        },
      }),

      // 6. Register update executor Sheets
      sheetsNode(
        "6",
        "Update risk register entry (Sheets)",
        1240,
        "Risk_Events",
        '[["{event_id}","{event_type}","{event_severity}","{affected_risk_area}","{event_description}","{new_risk_level}","{new_risk_score}","{score_direction}","{score_delta}","{risk_level_change}","{recommended_actions}","{reporter_name}","{update_timestamp}"]]',
      ),

      // 7. Change notification Slack
      slackNode(
        "7",
        "Notify risk team of register update (Slack)",
        1480,
        ":warning: *Risk Register Updated — {event_id}*\n*Event:* {event_type} ({event_severity})\n*Area:* {affected_risk_area}\n*Description:* {event_description}\n\n*Score Change:* {update_summary}\n*Level:* {risk_level_change}\n*Likelihood:* {likelihood_change} | Impact: {impact_change}\n\n*New Risks:* {new_risks_identified}\n*Control Effectiveness:* {control_effectiveness_change}\n*Recommended Actions:* {recommended_actions}\n*Reported by:* {reporter_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1418. SLA MONITOR (8 nodes)
  // ===================================================================

  "SLA Monitor": {
    summary:
      "Runs hourly to fetch ticket data from Jira, calculates SLA compliance in code, evaluates at-risk tickets via condition, escalates breaches to Slack (Level 1) and email (Level 2), sends proactive customer communications for breached SLAs, and maintains an SLA compliance dashboard in Sheets.",
    nodes: [
      trigger("Schedule: Hourly SLA compliance check", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Fires every hour to scan all open tickets for SLA compliance, identify at-risk and breached tickets, escalate appropriately, and maintain the SLA compliance dashboard",
        setup:
          "Configure Jira API credentials for ticket data. Set up Slack webhook for Level 1 escalation. Configure email for Level 2 escalation and customer communication. Set Google Sheets for SLA dashboard. Define SLA thresholds by priority (P1: 4h, P2: 8h, P3: 24h, P4: 72h).",
        inputFields: [],
      }),

      // 2. Ticket data fetch — Jira API
      n("2", "hitApi", "Fetch open tickets from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=status!=Done+AND+status!=Closed+ORDER+BY+created+ASC&maxResults=50&fields=summary,priority,status,created,assignee,customfield_10001",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "total_open_tickets", type: "number" },
            {
              name: "issues[0].key",
              customName: "oldest_ticket_key",
              type: "string",
            },
            {
              name: "issues[0].fields.summary",
              customName: "oldest_ticket_summary",
              type: "string",
            },
            {
              name: "issues[0].fields.priority.name",
              customName: "oldest_ticket_priority",
              type: "string",
            },
            {
              name: "issues[0].fields.created",
              customName: "oldest_ticket_created",
              type: "string",
            },
            {
              name: "issues[0].fields.assignee.displayName",
              customName: "oldest_ticket_assignee",
              type: "string",
            },
            {
              name: "issues[0].fields.status.name",
              customName: "oldest_ticket_status",
              type: "string",
            },
          ],
        },
      }),

      // 3. SLA calculator runCode
      n("3", "runCode", "Calculate SLA compliance metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate SLA compliance for the oldest/most at-risk ticket
const created = new Date(vars.oldest_ticket_created);
const now = new Date();
const elapsedHours = Math.round((now.getTime() - created.getTime()) / (1000 * 60 * 60));
const priority = (vars.oldest_ticket_priority || "Medium").toLowerCase();
const slaThresholds = { highest: 2, high: 4, medium: 8, low: 24, lowest: 72 };
const slaHours = slaThresholds[priority] || 24;
const remainingHours = slaHours - elapsedHours;
const slaPercentUsed = Math.round((elapsedHours / slaHours) * 100);
const slaStatus = remainingHours <= 0 ? "BREACHED" : slaPercentUsed >= 80 ? "AT_RISK" : "ON_TRACK";
const isAtRisk = slaStatus === "AT_RISK" || slaStatus === "BREACHED";
const breachLevel = slaStatus === "BREACHED" ? (elapsedHours > slaHours * 2 ? "LEVEL_2" : "LEVEL_1") : "NONE";
return {
  elapsed_hours: elapsedHours,
  sla_threshold_hours: slaHours,
  remaining_hours: remainingHours,
  sla_percent_used: slaPercentUsed,
  sla_status: slaStatus,
  is_at_risk: isAtRisk,
  breach_level: breachLevel,
  check_timestamp: now.toISOString(),
};`,
          outputs: [
            { name: "elapsed_hours", type: "number" },
            { name: "sla_threshold_hours", type: "number" },
            { name: "remaining_hours", type: "number" },
            { name: "sla_percent_used", type: "number" },
            { name: "sla_status", type: "string" },
            { name: "is_at_risk", type: "boolean" },
            { name: "breach_level", type: "string" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),

      // 4. At-risk alert condition
      n("4", "condition", "Check: Is ticket at risk or breached?", 760, {
        conditionData: [
          { variable: "is_at_risk", operator: "equals", value: "true" },
        ],
      }),

      // 5. Breach escalation Level 1 Slack (True branch)
      slackNode(
        "5",
        "Escalate SLA breach Level 1 (Slack)",
        1000,
        ":clock1: *SLA Alert — {sla_status}*\n*Ticket:* {oldest_ticket_key}: {oldest_ticket_summary}\n*Priority:* {oldest_ticket_priority} (SLA: {sla_threshold_hours}h)\n*Elapsed:* {elapsed_hours}h / {sla_threshold_hours}h ({sla_percent_used}% used)\n*Remaining:* {remaining_hours}h\n*Assignee:* {oldest_ticket_assignee} | Status: {oldest_ticket_status}\n*Breach Level:* {breach_level}\n\nPlease address this ticket immediately.",
        -220,
      ),

      // 6. Breach escalation Level 2 email (True branch, severe)
      emailNode(
        "6",
        "Escalate SLA breach Level 2 (Email)",
        1240,
        "{escalation_manager_email}",
        "SLA BREACH: {oldest_ticket_key} — {sla_status} ({sla_percent_used}% SLA consumed)",
        "<h2>SLA Breach Escalation — Level 2</h2><p><strong>Ticket:</strong> {oldest_ticket_key}: {oldest_ticket_summary}</p><p><strong>Priority:</strong> {oldest_ticket_priority} | SLA Threshold: {sla_threshold_hours} hours</p><p><strong>Elapsed:</strong> {elapsed_hours} hours ({sla_percent_used}% of SLA consumed)</p><p><strong>Remaining:</strong> {remaining_hours} hours</p><p><strong>Assignee:</strong> {oldest_ticket_assignee}</p><p><strong>Status:</strong> {oldest_ticket_status}</p><p><strong>Breach Level:</strong> {breach_level}</p><p>This ticket requires immediate management intervention. The SLA has been significantly exceeded.</p>",
        -220,
      ),

      // 7. Customer communication email (True branch)
      emailNode(
        "7",
        "Send proactive customer SLA update",
        1480,
        "{customer_email}",
        "Update on your request: {oldest_ticket_key}",
        "<p>We wanted to provide a proactive update on your support request <strong>{oldest_ticket_key}: {oldest_ticket_summary}</strong>.</p><p>Our team is actively working on this issue. Current status: <strong>{oldest_ticket_status}</strong>. Your assigned specialist, <strong>{oldest_ticket_assignee}</strong>, is prioritizing resolution.</p><p>We appreciate your patience and will provide another update shortly.</p>",
        380,
      ),

      // 8. SLA compliance dashboard Sheets
      sheetsNode(
        "8",
        "Update SLA compliance dashboard (Sheets)",
        1720,
        "SLA_Dashboard",
        '[["{check_timestamp}","{oldest_ticket_key}","{oldest_ticket_summary}","{oldest_ticket_priority}","{oldest_ticket_assignee}","{oldest_ticket_status}","{sla_threshold_hours}","{elapsed_hours}","{remaining_hours}","{sla_percent_used}","{sla_status}","{breach_level}","{total_open_tickets}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1419. SMS APPOINTMENT REMINDER SYSTEM (7 nodes)
  // ===================================================================

  "SMS Appointment Reminder System": {
    summary:
      "Receives appointment sync events via webhook, extracts appointment data with AI, calculates optimal reminder timing in code, personalizes messages with AI, delivers SMS via Twilio, checks for confirmation responses via condition, and follows up on no-shows by email.",
    nodes: [
      trigger("Webhook: Appointment synced", "webhook_app", {
        source: "calendar_system",
        event: "appointment.created",
        howItStarts:
          "Fires when a new appointment is created or synced from a scheduling system (Calendly, Acuity, Google Calendar, etc.), triggering the SMS reminder sequence",
        setup:
          "Configure webhook from your scheduling platform. Set OpenAI API key for message personalization. Configure Twilio API for SMS delivery. Set up email for no-show follow-up.",
        inputFields: [],
      }),

      // 2. AI extract appointment data
      aiNode(
        "2",
        "AI: Extract appointment details",
        280,
        "You are an appointment scheduling assistant. Extract the appointment details from the raw webhook payload regardless of format. Find the patient/client name, phone number, email, appointment date, appointment time, appointment type (consultation, follow-up, initial, procedure), provider name, location, duration, and any special instructions. Respond JSON: {client_name: string, client_phone: string, client_email: string, appointment_date: string, appointment_time: string, appointment_type: string, provider_name: string, location: string, duration_minutes: number, special_instructions: string}",
        "Raw appointment webhook payload: {webhook_payload}",
        [
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_phone", as: "client_phone", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "appointment_date", as: "appointment_date", type: "string" },
          { field: "appointment_time", as: "appointment_time", type: "string" },
          { field: "appointment_type", as: "appointment_type", type: "string" },
          { field: "provider_name", as: "provider_name", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          {
            field: "special_instructions",
            as: "special_instructions",
            type: "string",
          },
        ],
      ),

      // 3. Reminder scheduler runCode
      n("3", "runCode", "Calculate optimal reminder timing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate reminder timing based on appointment details
const apptDate = new Date(vars.appointment_date + "T" + vars.appointment_time);
const now = new Date();
const hoursUntilAppt = Math.round((apptDate.getTime() - now.getTime()) / (1000 * 60 * 60));
const apptType = (vars.appointment_type || "general").toLowerCase();
let reminderWindow = "24h";
if (hoursUntilAppt > 72) reminderWindow = "48h";
else if (hoursUntilAppt <= 24) reminderWindow = "2h";
const confirmationDeadline = new Date(apptDate.getTime() - 4 * 60 * 60 * 1000);
const formattedDate = apptDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const formattedTime = apptDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const reminderId = "APT-" + Date.now().toString(36).toUpperCase();
return {
  reminder_id: reminderId,
  hours_until_appointment: hoursUntilAppt,
  reminder_window: reminderWindow,
  formatted_date: formattedDate,
  formatted_time: formattedTime,
  confirmation_deadline: confirmationDeadline.toISOString(),
  needs_confirmation: hoursUntilAppt > 12 ? "yes" : "no",
};`,
          outputs: [
            { name: "reminder_id", type: "string" },
            { name: "hours_until_appointment", type: "number" },
            { name: "reminder_window", type: "string" },
            { name: "formatted_date", type: "string" },
            { name: "formatted_time", type: "string" },
            { name: "confirmation_deadline", type: "string" },
            { name: "needs_confirmation", type: "string" },
          ],
        },
      }),

      // 4. AI message personalizer
      aiNode(
        "4",
        "AI: Personalize SMS reminder",
        760,
        "You are an appointment reminder specialist. Write a friendly, professional SMS message for the appointment reminder. Keep it under 160 characters if possible. Include the client name, date, time, location, and provider. If there are special instructions, mention them briefly. End with a confirmation prompt (reply YES to confirm). Respond JSON: {sms_message: string, message_length: number}",
        "Client: {client_name}. Date: {formatted_date} at {formatted_time}. Provider: {provider_name}. Location: {location}. Type: {appointment_type}. Duration: {duration_minutes} min. Instructions: {special_instructions}. Reminder window: {reminder_window}.",
        [
          { field: "sms_message", as: "sms_message", type: "string" },
          { field: "message_length", as: "message_length", type: "number" },
        ],
      ),

      // 5. SMS delivery Twilio
      n("5", "hitApi", "Send SMS reminder via Twilio", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{client_phone}" },
            { key: "From", value: "{twilio_phone_number}" },
            { key: "Body", value: "{sms_message}" },
          ],
          variables: [
            { name: "sid", customName: "twilio_message_sid", type: "string" },
            {
              name: "status",
              customName: "twilio_delivery_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Response handler condition
      n("6", "condition", "Check: Does appointment need confirmation?", 1240, {
        conditionData: [
          { variable: "needs_confirmation", operator: "equals", value: "yes" },
        ],
      }),

      // 7. No-show follow-up email
      emailNode(
        "7",
        "Send no-show follow-up email",
        1480,
        "{client_email}",
        "We missed you — Reschedule your {appointment_type} appointment",
        "<p>Hi {client_name},</p><p>We noticed you may have missed your <strong>{appointment_type}</strong> appointment with <strong>{provider_name}</strong> scheduled for <strong>{formatted_date}</strong> at <strong>{formatted_time}</strong>.</p><p>We understand things come up. Please reply to this email or call us to reschedule at your earliest convenience.</p><p><strong>Location:</strong> {location}</p><p><strong>Reference:</strong> {reminder_id}</p><p>We look forward to seeing you soon.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1420. SOP DOCUMENT GENERATOR (8 nodes)
  // ===================================================================

  "SOP Document Generator": {
    summary:
      "Receives SOP creation requests via webhook, extracts request data with AI, searches for existing SOPs via API, drafts the SOP document with AI, cross-references compliance requirements in code, routes through SME approval gate, manages revisions in code, and distributes the published SOP via email.",
    nodes: [
      trigger("Webhook: SOP creation requested", "webhook_app", {
        source: "knowledge_base",
        event: "sop.request_created",
        howItStarts:
          "Fires when a new Standard Operating Procedure request is submitted, typically for new processes, updated regulations, or process improvement initiatives",
        setup:
          "Configure webhook from your knowledge management or ticketing system. Set OpenAI API key for SOP drafting. Configure your document repository API for existing SOP search. Set up email for publication distribution.",
        inputFields: [],
      }),

      // 2. AI extract SOP data
      aiNode(
        "2",
        "AI: Extract SOP request details",
        280,
        "You are a technical writing coordinator. Extract the SOP request details from the raw webhook payload regardless of format. Find the process name, department, requester name, requester email, process description, intended audience, compliance requirements, current pain points, and desired outcome. Respond JSON: {process_name: string, department: string, requester_name: string, requester_email: string, process_description: string, intended_audience: string, compliance_requirements: string, current_pain_points: string, desired_outcome: string, urgency: string}",
        "Raw SOP request payload: {webhook_payload}",
        [
          { field: "process_name", as: "process_name", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          {
            field: "process_description",
            as: "process_description",
            type: "string",
          },
          {
            field: "intended_audience",
            as: "intended_audience",
            type: "string",
          },
          {
            field: "compliance_requirements",
            as: "compliance_requirements",
            type: "string",
          },
          {
            field: "current_pain_points",
            as: "current_pain_points",
            type: "string",
          },
          { field: "desired_outcome", as: "desired_outcome", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Existing SOP search API
      n("3", "hitApi", "Search for existing SOPs via Confluence", 520, {
        hitApiDetails: {
          method: "GET",
          url: 'https://{confluence_domain}.atlassian.net/wiki/rest/api/content/search?cql=type=page+AND+label=sop+AND+text~"{process_name}"&limit=5',
          headers: [
            { key: "Authorization", value: "Basic {confluence_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "size", customName: "existing_sop_count", type: "number" },
            {
              name: "results[0].title",
              customName: "closest_sop_title",
              type: "string",
            },
            {
              name: "results[0].id",
              customName: "closest_sop_id",
              type: "string",
            },
            {
              name: "results[0]._links.webui",
              customName: "closest_sop_url",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI SOP drafter
      aiNode(
        "4",
        "AI: Draft SOP document",
        760,
        "You are a senior technical writer specializing in SOPs. Draft a comprehensive Standard Operating Procedure following ISO 9001 documentation standards. Include: purpose, scope, responsibilities, definitions, procedure steps (numbered with sub-steps), quality controls, safety considerations, exception handling, revision history section, and references. Use clear, imperative language. Respond JSON: {sop_title: string, sop_number: string, purpose: string, scope: string, responsibilities: string, definitions: string, procedure_steps: string, quality_controls: string, safety_considerations: string, exception_handling: string, document_version: string}",
        "Process: {process_name}. Department: {department}. Description: {process_description}. Audience: {intended_audience}. Compliance: {compliance_requirements}. Pain points: {current_pain_points}. Desired outcome: {desired_outcome}. Existing similar SOPs: {existing_sop_count} found (closest: {closest_sop_title}).",
        [
          { field: "sop_title", as: "sop_title", type: "string" },
          { field: "sop_number", as: "sop_number", type: "string" },
          { field: "purpose", as: "sop_purpose", type: "string" },
          { field: "scope", as: "sop_scope", type: "string" },
          {
            field: "responsibilities",
            as: "sop_responsibilities",
            type: "string",
          },
          {
            field: "procedure_steps",
            as: "sop_procedure_steps",
            type: "string",
          },
          {
            field: "quality_controls",
            as: "sop_quality_controls",
            type: "string",
          },
          {
            field: "exception_handling",
            as: "sop_exception_handling",
            type: "string",
          },
          { field: "document_version", as: "sop_version", type: "string" },
        ],
      ),

      // 5. Compliance cross-reference runCode
      n("5", "runCode", "Cross-reference compliance requirements", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Cross-reference SOP against compliance requirements
const complianceReqs = (vars.compliance_requirements || "").split(",").map(r => r.trim()).filter(Boolean);
const qualityControls = vars.sop_quality_controls || "";
const coveredReqs = complianceReqs.filter(req => qualityControls.toLowerCase().includes(req.toLowerCase().substring(0, 5)));
const uncoveredReqs = complianceReqs.filter(req => !qualityControls.toLowerCase().includes(req.toLowerCase().substring(0, 5)));
const complianceCoverage = complianceReqs.length > 0 ? Math.round((coveredReqs.length / complianceReqs.length) * 100) : 100;
const isCompliant = complianceCoverage >= 80;
const docId = "SOP-" + Date.now().toString(36).toUpperCase();
return {
  document_id: docId,
  compliance_coverage: complianceCoverage + "%",
  covered_requirements: coveredReqs.join(", ") || "all",
  uncovered_requirements: uncoveredReqs.join(", ") || "none",
  is_compliant: isCompliant,
  review_needed: !isCompliant || uncoveredReqs.length > 0,
  cross_reference_date: new Date().toISOString(),
};`,
          outputs: [
            { name: "document_id", type: "string" },
            { name: "compliance_coverage", type: "string" },
            { name: "covered_requirements", type: "string" },
            { name: "uncovered_requirements", type: "string" },
            { name: "is_compliant", type: "boolean" },
            { name: "review_needed", type: "boolean" },
            { name: "cross_reference_date", type: "string" },
          ],
        },
      }),

      // 6. SME review approval gate
      n("6", "approvalGate", "SME Review: Approve SOP draft", 1240, {
        approvalGateData: {
          message:
            "SOP Review Required — {document_id}\n\nTitle: {sop_title} ({sop_number})\nProcess: {process_name}\nDepartment: {department}\nRequester: {requester_name}\n\nPurpose: {sop_purpose}\nScope: {sop_scope}\n\nProcedure Steps:\n{sop_procedure_steps}\n\nQuality Controls: {sop_quality_controls}\nException Handling: {sop_exception_handling}\n\nCompliance Coverage: {compliance_coverage}\nUncovered Requirements: {uncovered_requirements}\nVersion: {sop_version}",
        },
      }),

      // 7. Revision manager runCode
      n("7", "runCode", "Finalize SOP revision metadata", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Finalize document metadata for publication
const publishDate = new Date().toISOString().split("T")[0];
const reviewCycle = "12"; // months until next review
const nextReviewDate = new Date();
nextReviewDate.setMonth(nextReviewDate.getMonth() + parseInt(reviewCycle));
const sopHtml = "<h1>" + vars.sop_title + "</h1>" +
  "<p><strong>Document ID:</strong> " + vars.document_id + " | <strong>Version:</strong> " + vars.sop_version + "</p>" +
  "<p><strong>Effective Date:</strong> " + publishDate + " | <strong>Next Review:</strong> " + nextReviewDate.toISOString().split("T")[0] + "</p>" +
  "<h2>Purpose</h2><p>" + vars.sop_purpose + "</p>" +
  "<h2>Scope</h2><p>" + vars.sop_scope + "</p>" +
  "<h2>Responsibilities</h2><p>" + vars.sop_responsibilities + "</p>" +
  "<h2>Procedure</h2><p>" + vars.sop_procedure_steps + "</p>" +
  "<h2>Quality Controls</h2><p>" + vars.sop_quality_controls + "</p>" +
  "<h2>Exceptions</h2><p>" + vars.sop_exception_handling + "</p>";
return {
  sop_html: sopHtml,
  publish_date: publishDate,
  next_review_date: nextReviewDate.toISOString().split("T")[0],
  review_cycle_months: reviewCycle,
  publication_status: "PUBLISHED",
};`,
          outputs: [
            { name: "sop_html", type: "string" },
            { name: "publish_date", type: "string" },
            { name: "next_review_date", type: "string" },
            { name: "review_cycle_months", type: "string" },
            { name: "publication_status", type: "string" },
          ],
        },
      }),

      // 8. Publication email
      emailNode(
        "8",
        "Distribute published SOP via email",
        1720,
        "{requester_email}",
        "SOP Published: {sop_title} ({document_id})",
        "<h2>Standard Operating Procedure Published</h2><p>Hi {requester_name},</p><p>The SOP you requested has been reviewed, approved, and published.</p><p><strong>Title:</strong> {sop_title}</p><p><strong>Document ID:</strong> {document_id} | Version: {sop_version}</p><p><strong>Effective Date:</strong> {publish_date}</p><p><strong>Next Review:</strong> {next_review_date}</p><p><strong>Compliance Coverage:</strong> {compliance_coverage}</p><hr/>{sop_html}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1421. SCHEDULED DATA BACKUP ALERT (7 nodes)
  // ===================================================================

  "Scheduled Data Backup Alert": {
    summary:
      "Runs daily to fetch backup job statuses from the backup API, validates data integrity in code, detects anomalies via condition, routes alerts to Slack, triggers recovery procedures for failures via API, and maintains a backup health dashboard in Sheets.",
    nodes: [
      trigger("Schedule: Daily backup health check", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Fires every day at 6 AM to verify that all scheduled backup jobs completed successfully, validate data integrity, detect anomalies, and alert on failures",
        setup:
          "Configure your backup platform API (Veeam, AWS Backup, Commvault, etc.). Set up Slack webhook for alerts. Configure recovery API endpoint. Set Google Sheets for backup health tracking.",
        inputFields: [],
      }),

      // 2. Backup status fetcher API
      n("2", "hitApi", "Fetch backup job statuses", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://backup.example.com/api/v1/jobs?status=all&period=last_24h",
          headers: [
            { key: "Authorization", value: "Bearer {backup_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "total_jobs",
              customName: "total_backup_jobs",
              type: "number",
            },
            { name: "completed", customName: "completed_jobs", type: "number" },
            { name: "failed", customName: "failed_jobs", type: "number" },
            { name: "warnings", customName: "warning_jobs", type: "number" },
            {
              name: "last_backup.name",
              customName: "last_backup_name",
              type: "string",
            },
            {
              name: "last_backup.size_gb",
              customName: "last_backup_size_gb",
              type: "string",
            },
            {
              name: "last_backup.duration_minutes",
              customName: "last_backup_duration",
              type: "string",
            },
            {
              name: "last_backup.status",
              customName: "last_backup_status",
              type: "string",
            },
          ],
        },
      }),

      // 3. Integrity validator runCode
      n("3", "runCode", "Validate backup integrity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate backup integrity and compute health score
const total = parseInt(vars.total_backup_jobs || "0", 10);
const completed = parseInt(vars.completed_jobs || "0", 10);
const failed = parseInt(vars.failed_jobs || "0", 10);
const warnings = parseInt(vars.warning_jobs || "0", 10);
const successRate = total > 0 ? Math.round((completed / total) * 100) : 0;
const healthScore = Math.max(0, 100 - (failed * 20) - (warnings * 5));
const hasAnomalies = failed > 0 || successRate < 95;
const backupStatus = failed > 0 ? "FAILURE_DETECTED" : warnings > 0 ? "WARNINGS" : "HEALTHY";
const sizeGb = parseFloat(vars.last_backup_size_gb || "0");
const durationMin = parseFloat(vars.last_backup_duration || "0");
const throughputGbPerMin = durationMin > 0 ? (sizeGb / durationMin).toFixed(2) : "0";
return {
  success_rate: successRate,
  health_score: healthScore,
  has_anomalies: hasAnomalies,
  backup_status: backupStatus,
  throughput: throughputGbPerMin + " GB/min",
  check_timestamp: new Date().toISOString(),
};`,
          outputs: [
            { name: "success_rate", type: "number" },
            { name: "health_score", type: "number" },
            { name: "has_anomalies", type: "boolean" },
            { name: "backup_status", type: "string" },
            { name: "throughput", type: "string" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Anomaly detector condition
      n("4", "condition", "Check: Are there backup anomalies?", 760, {
        conditionData: [
          { variable: "has_anomalies", operator: "equals", value: "true" },
        ],
      }),

      // 5. Alert router Slack (True branch)
      slackNode(
        "5",
        "Alert backup anomaly (Slack)",
        1000,
        ":floppy_disk: *Backup Alert — {backup_status}*\n*Health Score:* {health_score}/100 | Success Rate: {success_rate}%\n*Jobs:* {total_backup_jobs} total | {completed_jobs} completed | {failed_jobs} failed | {warning_jobs} warnings\n*Last Backup:* {last_backup_name} ({last_backup_size_gb} GB in {last_backup_duration} min)\n*Throughput:* {throughput}\n*Last Status:* {last_backup_status}\n\nImmediate investigation required for failed jobs.",
        -220,
      ),

      // 6. Failure recovery trigger API (True branch)
      n("6", "hitApi", "Trigger backup recovery procedure", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://backup.example.com/api/v1/jobs/retry",
          headers: [
            { key: "Authorization", value: "Bearer {backup_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter", value: '{"status":"failed","period":"last_24h"}' },
            { key: "action", value: "retry_failed" },
          ],
          variables: [
            {
              name: "retried_count",
              customName: "retried_jobs_count",
              type: "number",
            },
            {
              name: "retry_status",
              customName: "recovery_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Backup health dashboard Sheets
      sheetsNode(
        "7",
        "Update backup health dashboard (Sheets)",
        1480,
        "Backup_Health",
        '[["{check_timestamp}","{total_backup_jobs}","{completed_jobs}","{failed_jobs}","{warning_jobs}","{success_rate}","{health_score}","{backup_status}","{last_backup_name}","{last_backup_size_gb}","{throughput}","{last_backup_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1422. SCOPE CHANGE REQUEST (8 nodes)
  // ===================================================================

  "Scope Change Request": {
    summary:
      "Receives change requests via webhook, extracts change data with AI, analyzes impact on timeline, budget, and resources in code, assesses risk with AI, notifies the change advisory board by email, waits for a review period, evaluates the voting decision via condition, and updates the project plan in Sheets.",
    nodes: [
      trigger("Webhook: Scope change request submitted", "webhook_app", {
        source: "project_management",
        event: "change_request.submitted",
        howItStarts:
          "Fires when a scope change request is submitted for any active project, including feature additions, requirement modifications, timeline changes, or resource reallocations",
        setup:
          "Configure webhook from your project management tool (Jira, Asana, Monday, etc.). Set OpenAI API key for impact and risk analysis. Configure email for CAB notification. Set Google Sheets for change log tracking.",
        inputFields: [],
      }),

      // 2. AI extract change data
      aiNode(
        "2",
        "AI: Extract change request details",
        280,
        "You are a project change management analyst. Extract the change request details from the raw webhook payload regardless of format. Find the project name, change title, change description, requester name, requester email, change type (scope_addition, requirement_change, timeline_change, resource_change, budget_change), priority, affected deliverables, and business justification. Respond JSON: {project_name: string, change_title: string, change_description: string, requester_name: string, requester_email: string, change_type: string, priority: string, affected_deliverables: string, business_justification: string, estimated_effort_hours: number}",
        "Raw change request payload: {webhook_payload}",
        [
          { field: "project_name", as: "project_name", type: "string" },
          { field: "change_title", as: "change_title", type: "string" },
          {
            field: "change_description",
            as: "change_description",
            type: "string",
          },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "priority", as: "change_priority", type: "string" },
          {
            field: "affected_deliverables",
            as: "affected_deliverables",
            type: "string",
          },
          {
            field: "business_justification",
            as: "business_justification",
            type: "string",
          },
          {
            field: "estimated_effort_hours",
            as: "estimated_effort_hours",
            type: "number",
          },
        ],
      ),

      // 3. Impact analysis engine runCode
      n("3", "runCode", "Analyze scope change impact", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze impact on timeline, budget, and resources
const effortHours = parseInt(vars.estimated_effort_hours || "40", 10);
const changeType = (vars.change_type || "scope_addition").toLowerCase();
const hoursPerDay = 8;
const costPerHour = 95;
const timelineImpactDays = Math.ceil(effortHours / hoursPerDay);
const budgetImpact = effortHours * costPerHour;
const resourceImpact = effortHours > 80 ? "ADDITIONAL_HEADCOUNT" : effortHours > 40 ? "REALLOCATION_NEEDED" : "ABSORBABLE";
const overallImpact = effortHours > 80 ? "HIGH" : effortHours > 20 ? "MEDIUM" : "LOW";
const changeId = "CR-" + Date.now().toString(36).toUpperCase();
const requiresSteeringApproval = budgetImpact > 10000 || timelineImpactDays > 10;
return {
  change_id: changeId,
  timeline_impact_days: timelineImpactDays,
  budget_impact: "$" + budgetImpact.toLocaleString(),
  budget_impact_raw: budgetImpact,
  resource_impact: resourceImpact,
  overall_impact: overallImpact,
  requires_steering_approval: requiresSteeringApproval,
  analysis_date: new Date().toISOString(),
};`,
          outputs: [
            { name: "change_id", type: "string" },
            { name: "timeline_impact_days", type: "number" },
            { name: "budget_impact", type: "string" },
            { name: "budget_impact_raw", type: "number" },
            { name: "resource_impact", type: "string" },
            { name: "overall_impact", type: "string" },
            { name: "requires_steering_approval", type: "boolean" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),

      // 4. AI risk assessment
      aiNode(
        "4",
        "AI: Assess change request risk",
        760,
        "You are a project risk analyst. Evaluate the risks associated with this scope change. Consider: technical complexity, dependency impacts, team capacity, deadline pressure, quality implications, and stakeholder satisfaction. Provide a risk rating and specific concerns. Respond JSON: {risk_rating: string, technical_risk: string, dependency_risk: string, capacity_risk: string, quality_risk: string, top_concern: string, risk_mitigation: string, go_no_go_recommendation: string}",
        "Project: {project_name}. Change: {change_title} ({change_type}). Effort: {estimated_effort_hours}h. Timeline impact: {timeline_impact_days} days. Budget: {budget_impact}. Resource: {resource_impact}. Overall: {overall_impact}. Deliverables affected: {affected_deliverables}. Justification: {business_justification}.",
        [
          { field: "risk_rating", as: "risk_rating", type: "string" },
          { field: "technical_risk", as: "technical_risk", type: "string" },
          { field: "dependency_risk", as: "dependency_risk", type: "string" },
          { field: "capacity_risk", as: "capacity_risk", type: "string" },
          { field: "quality_risk", as: "quality_risk", type: "string" },
          { field: "top_concern", as: "top_concern", type: "string" },
          { field: "risk_mitigation", as: "risk_mitigation", type: "string" },
          {
            field: "go_no_go_recommendation",
            as: "go_no_go_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. Change advisory board notification email
      emailNode(
        "5",
        "Notify change advisory board",
        1000,
        "{cab_email}",
        "Change Request {change_id}: {change_title} — {project_name} ({overall_impact} impact)",
        "<h2>Change Request — {change_id}</h2><p><strong>Project:</strong> {project_name}</p><p><strong>Change:</strong> {change_title}</p><p><strong>Type:</strong> {change_type} | Priority: {change_priority}</p><p><strong>Requested by:</strong> {requester_name}</p><hr/><h3>Impact Analysis</h3><p><strong>Timeline:</strong> +{timeline_impact_days} days | <strong>Budget:</strong> {budget_impact} | <strong>Resources:</strong> {resource_impact}</p><p><strong>Overall Impact:</strong> {overall_impact}</p><h3>Risk Assessment</h3><p><strong>Rating:</strong> {risk_rating}</p><p><strong>Top Concern:</strong> {top_concern}</p><p><strong>Recommendation:</strong> {go_no_go_recommendation}</p><p><strong>Mitigation:</strong> {risk_mitigation}</p><h3>Business Justification</h3><p>{business_justification}</p><p><strong>Affected Deliverables:</strong> {affected_deliverables}</p><p><em>Please vote APPROVE or REJECT within 5 business days.</em></p>",
      ),

      // 6. Delay 5 days for review
      n("6", "delayAction", "Wait 5 business days for CAB review", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Voting decision condition
      n("7", "condition", "Check: Is overall impact HIGH?", 1480, {
        conditionData: [
          { variable: "overall_impact", operator: "equals", value: "HIGH" },
        ],
      }),

      // 8. Project plan update Sheets
      sheetsNode(
        "8",
        "Log change request decision (Sheets)",
        1720,
        "Change_Requests",
        '[["{change_id}","{project_name}","{change_title}","{change_type}","{change_priority}","{requester_name}","{overall_impact}","{timeline_impact_days}","{budget_impact}","{resource_impact}","{risk_rating}","{go_no_go_recommendation}","{business_justification}","{analysis_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1423. SCOPE CREEP DETECTOR (7 nodes)
  // ===================================================================

  "Scope Creep Detector": {
    summary:
      "Runs daily to fetch current sprint data from Jira, compares against the original baseline in code, uses AI to identify scope creep patterns, evaluates threshold alerts via condition, tags root causes in code, and generates trend reports in Sheets.",
    nodes: [
      trigger("Schedule: Daily scope creep analysis", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Fires every weekday at 9 AM to compare current sprint scope against the committed baseline, detect unauthorized additions, and flag scope creep patterns before they derail the project",
        setup:
          "Configure Jira API credentials for sprint data access. Set OpenAI API key for pattern analysis. Set up Slack webhook for threshold alerts. Set Google Sheets for trend reporting. Define your sprint commitment baseline.",
        inputFields: [],
      }),

      // 2. Sprint data fetch — Jira API
      n("2", "hitApi", "Fetch current sprint data from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/agile/1.0/board/{board_id}/sprint?state=active",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values[0].name",
              customName: "sprint_name",
              type: "string",
            },
            { name: "values[0].id", customName: "sprint_id", type: "string" },
            {
              name: "values[0].startDate",
              customName: "sprint_start",
              type: "string",
            },
            {
              name: "values[0].endDate",
              customName: "sprint_end",
              type: "string",
            },
            {
              name: "values[0].goal",
              customName: "sprint_goal",
              type: "string",
            },
          ],
        },
      }),

      // 3. Baseline comparison engine runCode
      n("3", "runCode", "Compare current scope against baseline", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current sprint scope against committed baseline
const sprintStart = new Date(vars.sprint_start);
const sprintEnd = new Date(vars.sprint_end);
const now = new Date();
const totalDays = Math.ceil((sprintEnd - sprintStart) / (1000 * 60 * 60 * 24));
const elapsedDays = Math.ceil((now - sprintStart) / (1000 * 60 * 60 * 24));
const sprintProgress = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
// Baseline values — in production, these would come from sprint planning data
const baselineStoryPoints = 40;
const baselineIssueCount = 15;
// Simulated current values — would come from Jira issue count query
const currentStoryPoints = 48;
const currentIssueCount = 19;
const pointsDelta = currentStoryPoints - baselineStoryPoints;
const issuesDelta = currentIssueCount - baselineIssueCount;
const scopeCreepPercent = Math.round((pointsDelta / baselineStoryPoints) * 100);
const hasCreep = scopeCreepPercent > 10;
const creepSeverity = scopeCreepPercent > 30 ? "CRITICAL" : scopeCreepPercent > 20 ? "HIGH" : scopeCreepPercent > 10 ? "MODERATE" : "NONE";
return {
  sprint_progress: sprintProgress,
  baseline_points: baselineStoryPoints,
  current_points: currentStoryPoints,
  points_delta: pointsDelta,
  baseline_issues: baselineIssueCount,
  current_issues: currentIssueCount,
  issues_delta: issuesDelta,
  scope_creep_percent: scopeCreepPercent,
  has_creep: hasCreep,
  creep_severity: creepSeverity,
  analysis_date: now.toISOString().split("T")[0],
};`,
          outputs: [
            { name: "sprint_progress", type: "number" },
            { name: "baseline_points", type: "number" },
            { name: "current_points", type: "number" },
            { name: "points_delta", type: "number" },
            { name: "baseline_issues", type: "number" },
            { name: "current_issues", type: "number" },
            { name: "issues_delta", type: "number" },
            { name: "scope_creep_percent", type: "number" },
            { name: "has_creep", type: "boolean" },
            { name: "creep_severity", type: "string" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),

      // 4. AI creep pattern analyzer
      aiNode(
        "4",
        "AI: Analyze scope creep patterns",
        760,
        "You are an agile project analyst specializing in scope management. Analyze the scope metrics to identify creep patterns, potential root causes, and provide recommendations. Consider: mid-sprint additions, requirement changes, gold plating, poor estimation, stakeholder pressure, and unclear acceptance criteria. Respond JSON: {pattern_identified: string, root_cause_analysis: string, top_root_cause: string, impact_on_delivery: string, recommendations: string, historical_trend: string, risk_to_sprint_goal: string}",
        "Sprint: {sprint_name}. Goal: {sprint_goal}. Progress: {sprint_progress}%. Baseline: {baseline_points} SP / {baseline_issues} issues. Current: {current_points} SP / {current_issues} issues. Delta: +{points_delta} SP / +{issues_delta} issues. Creep: {scope_creep_percent}%. Severity: {creep_severity}.",
        [
          {
            field: "pattern_identified",
            as: "pattern_identified",
            type: "string",
          },
          {
            field: "root_cause_analysis",
            as: "root_cause_analysis",
            type: "string",
          },
          { field: "top_root_cause", as: "top_root_cause", type: "string" },
          {
            field: "impact_on_delivery",
            as: "impact_on_delivery",
            type: "string",
          },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "historical_trend", as: "historical_trend", type: "string" },
          {
            field: "risk_to_sprint_goal",
            as: "risk_to_sprint_goal",
            type: "string",
          },
        ],
      ),

      // 5. Threshold alert condition
      n("5", "condition", "Check: Is scope creep above 10%?", 1000, {
        conditionData: [
          { variable: "has_creep", operator: "equals", value: "true" },
        ],
      }),

      // 6. Root cause tagger runCode
      n("6", "runCode", "Tag root causes and generate alert", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Tag root causes and prepare alert data
const severity = vars.creep_severity || "NONE";
const rootCause = vars.top_root_cause || "unidentified";
const tags = [severity, rootCause.replace(/\\s+/g, "_").toUpperCase()];
if (parseInt(vars.scope_creep_percent || "0", 10) > 20) tags.push("URGENT");
if (vars.risk_to_sprint_goal && vars.risk_to_sprint_goal.toLowerCase().includes("high")) tags.push("SPRINT_AT_RISK");
const alertMessage = ":mag: *Scope Creep Alert — " + vars.sprint_name + "*\\n" +
  "*Severity:* " + severity + " (" + vars.scope_creep_percent + "% over baseline)\\n" +
  "*Points:* " + vars.baseline_points + " → " + vars.current_points + " (+" + vars.points_delta + ")\\n" +
  "*Issues:* " + vars.baseline_issues + " → " + vars.current_issues + " (+" + vars.issues_delta + ")\\n" +
  "*Root Cause:* " + rootCause + "\\n" +
  "*Pattern:* " + (vars.pattern_identified || "N/A") + "\\n" +
  "*Sprint Goal Risk:* " + (vars.risk_to_sprint_goal || "N/A") + "\\n" +
  "*Recommendations:* " + (vars.recommendations || "N/A");
return {
  tags: tags.join(", "),
  alert_message: alertMessage,
  tagged_timestamp: new Date().toISOString(),
};`,
          outputs: [
            { name: "tags", type: "string" },
            { name: "alert_message", type: "string" },
            { name: "tagged_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Trend reporter Sheets
      sheetsNode(
        "7",
        "Log scope creep trend data (Sheets)",
        1480,
        "Scope_Creep_Trends",
        '[["{analysis_date}","{sprint_name}","{sprint_progress}","{baseline_points}","{current_points}","{points_delta}","{scope_creep_percent}","{creep_severity}","{top_root_cause}","{pattern_identified}","{risk_to_sprint_goal}","{tags}","{recommendations}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1424. SERVICE FEEDBACK EMAIL (7 nodes)
  // ===================================================================

  "Service Feedback Email": {
    summary:
      "Receives service completion events via webhook, extracts service data with AI, fetches customer context from HubSpot, generates a personalized feedback request with AI, delivers the feedback email, evaluates response via condition, and logs analytics to Sheets.",
    nodes: [
      trigger("Webhook: Service completed", "webhook_app", {
        source: "service_platform",
        event: "service.completed",
        howItStarts:
          "Fires when a service engagement is marked as completed — such as a support ticket resolution, consulting session, installation, repair, or delivery — triggering a personalized feedback collection email",
        setup:
          "Configure webhook from your service delivery platform. Set OpenAI API key for content personalization. Configure HubSpot for customer context. Set up email delivery. Set Google Sheets for feedback analytics.",
        inputFields: [],
      }),

      // 2. AI extract service data
      aiNode(
        "2",
        "AI: Extract service completion details",
        280,
        "You are a customer experience analyst. Extract the service completion details from the raw webhook payload regardless of format. Find the customer name, customer email, service type, service description, technician or agent name, resolution summary, service date, duration, and any satisfaction indicators. Respond JSON: {customer_name: string, customer_email: string, service_type: string, service_description: string, agent_name: string, resolution_summary: string, service_date: string, service_duration: string, ticket_id: string, service_outcome: string}",
        "Raw service completion payload: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "service_type", as: "service_type", type: "string" },
          {
            field: "service_description",
            as: "service_description",
            type: "string",
          },
          { field: "agent_name", as: "agent_name", type: "string" },
          {
            field: "resolution_summary",
            as: "resolution_summary",
            type: "string",
          },
          { field: "service_date", as: "service_date", type: "string" },
          { field: "service_duration", as: "service_duration", type: "string" },
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "service_outcome", as: "service_outcome", type: "string" },
        ],
      ),

      // 3. Customer context fetch HubSpot
      hubspotGetNode(
        "3",
        "Fetch customer context from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].properties.lifecyclestage",
            customName: "customer_lifecycle",
            type: "string",
          },
          {
            name: "results[0].properties.hs_object_id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "customer_company",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "total_deals",
            type: "string",
          },
        ],
      ),

      // 4. AI feedback request composer
      aiNode(
        "4",
        "AI: Compose personalized feedback request",
        760,
        "You are a customer experience writer. Compose a warm, personalized feedback request email for the completed service. Reference the specific service performed, the agent who helped, and the outcome. Include a clear call-to-action with a satisfaction rating prompt (1-5 stars). Keep the tone appreciative and brief. Respond JSON: {email_subject: string, email_body: string, survey_url_params: string}",
        "Customer: {customer_name} ({customer_company}). Service: {service_type} — {service_description}. Agent: {agent_name}. Resolution: {resolution_summary}. Date: {service_date}. Duration: {service_duration}. Outcome: {service_outcome}. Lifecycle: {customer_lifecycle}. Ticket: {ticket_id}.",
        [
          {
            field: "email_subject",
            as: "feedback_email_subject",
            type: "string",
          },
          { field: "email_body", as: "feedback_email_body", type: "string" },
          {
            field: "survey_url_params",
            as: "survey_url_params",
            type: "string",
          },
        ],
      ),

      // 5. Feedback email delivery
      emailNode(
        "5",
        "Send feedback request email",
        1000,
        "{customer_email}",
        "{feedback_email_subject}",
        "{feedback_email_body}",
      ),

      // 6. Response processor condition
      n("6", "condition", "Check: Was service outcome positive?", 1240, {
        conditionData: [
          {
            variable: "service_outcome",
            operator: "contains",
            value: "resolved",
          },
        ],
      }),

      // 7. Analytics reporting Sheets
      sheetsNode(
        "7",
        "Log feedback analytics (Sheets)",
        1480,
        "Feedback_Analytics",
        '[["{ticket_id}","{customer_name}","{customer_email}","{customer_company}","{service_type}","{agent_name}","{service_date}","{service_duration}","{service_outcome}","{resolution_summary}","{customer_lifecycle}","{survey_url_params}","sent","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1425. SERVICE STATUS PAGE BOT (8 nodes)
  // ===================================================================

  "Service Status Page Bot": {
    summary:
      "Receives status queries via webhook, extracts the query with AI, fetches live status from StatusPage API, interprets the status with AI, retrieves historical data from StatusPage, broadcasts proactive updates to Slack, automates incident updates via API, and logs uptime reports in Sheets.",
    nodes: [
      trigger("Webhook: Service status query received", "webhook_app", {
        source: "chatbot_platform",
        event: "status.query_received",
        howItStarts:
          "Fires when a user or internal system queries the current service status — typically from a chatbot, internal tool, or monitoring system requesting health information",
        setup:
          "Configure webhook from your chatbot or query interface. Set OpenAI API key for query interpretation and status translation. Configure Atlassian StatusPage API. Set up Slack webhook for status broadcasting. Set Google Sheets for uptime reports.",
        inputFields: [],
      }),

      // 2. AI extract query
      aiNode(
        "2",
        "AI: Extract status query intent",
        280,
        "You are a service desk assistant. Extract the query details from the raw webhook payload regardless of format. Determine what service the user is asking about, the query type (current_status, incident_history, maintenance_schedule, uptime_report), the requester identity, and any specific time range. Respond JSON: {service_name: string, query_type: string, requester_name: string, requester_channel: string, time_range: string, specific_component: string, urgency: string}",
        "Raw status query payload: {webhook_payload}",
        [
          { field: "service_name", as: "service_name", type: "string" },
          { field: "query_type", as: "query_type", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "requester_channel",
            as: "requester_channel",
            type: "string",
          },
          { field: "time_range", as: "time_range", type: "string" },
          {
            field: "specific_component",
            as: "specific_component",
            type: "string",
          },
          { field: "urgency", as: "query_urgency", type: "string" },
        ],
      ),

      // 3. Live status fetcher — StatusPage API
      n("3", "hitApi", "Fetch live status from StatusPage", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.statuspage.io/v1/pages/{statuspage_page_id}/components",
          headers: [
            { key: "Authorization", value: "OAuth {statuspage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "[0].name",
              customName: "component_1_name",
              type: "string",
            },
            {
              name: "[0].status",
              customName: "component_1_status",
              type: "string",
            },
            {
              name: "[1].name",
              customName: "component_2_name",
              type: "string",
            },
            {
              name: "[1].status",
              customName: "component_2_status",
              type: "string",
            },
            {
              name: "[2].name",
              customName: "component_3_name",
              type: "string",
            },
            {
              name: "[2].status",
              customName: "component_3_status",
              type: "string",
            },
            { name: "length", customName: "total_components", type: "number" },
          ],
        },
      }),

      // 4. AI status interpreter
      aiNode(
        "4",
        "AI: Interpret and summarize status",
        760,
        "You are a technical communications specialist. Interpret the raw component status data and create a human-readable summary. Translate status codes (operational, degraded_performance, partial_outage, major_outage) into clear language. Assess overall system health. Recommend any actions the user should take. Respond JSON: {overall_status: string, status_summary: string, affected_services: string, user_impact: string, recommended_actions: string, estimated_resolution: string, status_emoji: string}",
        "Query about: {service_name}. Components: {component_1_name}: {component_1_status}, {component_2_name}: {component_2_status}, {component_3_name}: {component_3_status}. Total components: {total_components}. Query type: {query_type}.",
        [
          { field: "overall_status", as: "overall_status", type: "string" },
          { field: "status_summary", as: "status_summary", type: "string" },
          {
            field: "affected_services",
            as: "affected_services",
            type: "string",
          },
          { field: "user_impact", as: "user_impact", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "estimated_resolution",
            as: "estimated_resolution",
            type: "string",
          },
          { field: "status_emoji", as: "status_emoji", type: "string" },
        ],
      ),

      // 5. Historical status lookup API
      n("5", "hitApi", "Fetch incident history from StatusPage", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.statuspage.io/v1/pages/{statuspage_page_id}/incidents?limit=5",
          headers: [
            { key: "Authorization", value: "OAuth {statuspage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "[0].name",
              customName: "recent_incident_name",
              type: "string",
            },
            {
              name: "[0].status",
              customName: "recent_incident_status",
              type: "string",
            },
            {
              name: "[0].created_at",
              customName: "recent_incident_date",
              type: "string",
            },
            {
              name: "[0].impact",
              customName: "recent_incident_impact",
              type: "string",
            },
            {
              name: "length",
              customName: "recent_incident_count",
              type: "number",
            },
          ],
        },
      }),

      // 6. Proactive status broadcaster Slack
      slackNode(
        "6",
        "Broadcast status update to Slack",
        1240,
        "{status_emoji} *Service Status Update*\n*Overall:* {overall_status}\n*Summary:* {status_summary}\n\n*Components:*\n- {component_1_name}: {component_1_status}\n- {component_2_name}: {component_2_status}\n- {component_3_name}: {component_3_status}\n\n*Affected:* {affected_services}\n*User Impact:* {user_impact}\n*Actions:* {recommended_actions}\n\n*Recent Incidents:* {recent_incident_count} in history\nLatest: {recent_incident_name} ({recent_incident_status})",
      ),

      // 7. Incident update automation API
      n("7", "hitApi", "Auto-update incident status if needed", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.statuspage.io/v1/pages/{statuspage_page_id}/incidents",
          headers: [
            { key: "Authorization", value: "OAuth {statuspage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "incident",
              value:
                '{"name":"Status Query Alert: {service_name}","status":"investigating","impact_override":"none","body":"Automated status check triggered by {requester_name} via {requester_channel}. Overall status: {overall_status}. Summary: {status_summary}"}',
            },
          ],
          variables: [
            { name: "id", customName: "incident_id", type: "string" },
            { name: "status", customName: "incident_status", type: "string" },
          ],
        },
      }),

      // 8. Uptime report Sheets
      sheetsNode(
        "8",
        "Log uptime report data (Sheets)",
        1720,
        "Uptime_Reports",
        '[["{service_name}","{overall_status}","{component_1_name}: {component_1_status}","{component_2_name}: {component_2_status}","{component_3_name}: {component_3_status}","{affected_services}","{user_impact}","{recent_incident_count}","{recent_incident_name}","{requester_name}","{query_type}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1426. SHIPMENT TRACKING NOTIFICATION BOT (8 nodes)
  // ===================================================================

  "Shipment Tracking Notification Bot": {
    summary:
      "Receives order fulfillment events via webhook, extracts tracking data with AI, integrates with AfterShip for carrier tracking, evaluates tracking updates via condition, translates status updates with AI for the customer, sends customer notification emails, escalates exceptions to Slack, and logs delivery confirmations in Sheets.",
    nodes: [
      trigger("Webhook: Order fulfilled and shipped", "webhook_app", {
        source: "ecommerce_platform",
        event: "order.fulfilled",
        howItStarts:
          "Fires when an order is marked as fulfilled and a shipping label/tracking number has been generated, triggering the customer notification and tracking monitoring sequence",
        setup:
          "Configure webhook from your e-commerce platform (Shopify, WooCommerce, BigCommerce). Set OpenAI API key for status translation. Configure AfterShip API for carrier tracking. Set up email for customer notifications. Configure Slack for exception alerts. Set Google Sheets for delivery tracking.",
        inputFields: [],
      }),

      // 2. AI extract tracking data
      aiNode(
        "2",
        "AI: Extract shipment tracking details",
        280,
        "You are a logistics data analyst. Extract the shipment and tracking details from the raw webhook payload regardless of format. Find the order ID, customer name, customer email, shipping address, carrier name, tracking number, estimated delivery date, items shipped, and total weight. Respond JSON: {order_id: string, customer_name: string, customer_email: string, shipping_address: string, carrier_name: string, tracking_number: string, estimated_delivery: string, items_shipped: string, total_weight: string, shipping_method: string}",
        "Raw order fulfillment payload: {webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "shipping_address", as: "shipping_address", type: "string" },
          { field: "carrier_name", as: "carrier_name", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "items_shipped", as: "items_shipped", type: "string" },
          { field: "total_weight", as: "total_weight", type: "string" },
          { field: "shipping_method", as: "shipping_method", type: "string" },
        ],
      ),

      // 3. Carrier integration — AfterShip API
      n("3", "hitApi", "Track shipment via AfterShip", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.aftership.com/v4/trackings",
          headers: [
            { key: "aftership-api-key", value: "{aftership_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "tracking",
              value:
                '{"tracking_number":"{tracking_number}","slug":"{carrier_name}","title":"Order {order_id}","customer_name":"{customer_name}","emails":["{customer_email}"]}',
            },
          ],
          variables: [
            {
              name: "data.tracking.id",
              customName: "aftership_tracking_id",
              type: "string",
            },
            {
              name: "data.tracking.tag",
              customName: "tracking_status_tag",
              type: "string",
            },
            {
              name: "data.tracking.subtag",
              customName: "tracking_subtag",
              type: "string",
            },
            {
              name: "data.tracking.expected_delivery",
              customName: "aftership_expected_delivery",
              type: "string",
            },
          ],
        },
      }),

      // 4. Tracking update listener condition
      n("4", "condition", "Check: Is tracking status an exception?", 760, {
        conditionData: [
          {
            variable: "tracking_status_tag",
            operator: "contains",
            value: "Exception",
          },
        ],
      }),

      // 5. AI status translator
      aiNode(
        "5",
        "AI: Translate tracking status for customer",
        1000,
        "You are a customer communications specialist for e-commerce. Translate the raw tracking status into a friendly, clear customer update. Include the current location/status, estimated delivery, and any actions the customer should take. Keep the tone warm and reassuring. Respond JSON: {status_message: string, delivery_estimate_text: string, customer_action: string, email_subject: string}",
        "Order: {order_id}. Customer: {customer_name}. Carrier: {carrier_name}. Tracking: {tracking_number}. Status: {tracking_status_tag} ({tracking_subtag}). Expected delivery: {aftership_expected_delivery}. Items: {items_shipped}. Method: {shipping_method}.",
        [
          { field: "status_message", as: "status_message", type: "string" },
          {
            field: "delivery_estimate_text",
            as: "delivery_estimate_text",
            type: "string",
          },
          { field: "customer_action", as: "customer_action", type: "string" },
          {
            field: "email_subject",
            as: "tracking_email_subject",
            type: "string",
          },
        ],
        380,
      ),

      // 6. Customer notification email (normal flow)
      emailNode(
        "6",
        "Send shipping notification to customer",
        1240,
        "{customer_email}",
        "{tracking_email_subject}",
        "<p>Hi {customer_name},</p><p>{status_message}</p><p><strong>Order:</strong> {order_id}</p><p><strong>Items:</strong> {items_shipped}</p><p><strong>Carrier:</strong> {carrier_name} | Tracking: {tracking_number}</p><p><strong>Estimated Delivery:</strong> {delivery_estimate_text}</p><p>{customer_action}</p><p><a href='https://track.aftership.com/{tracking_number}'>Track Your Package</a></p>",
        380,
      ),

      // 7. Exception handler Slack (exception branch)
      slackNode(
        "7",
        "Escalate shipping exception (Slack)",
        1240,
        ":package: *Shipping Exception Alert*\n*Order:* {order_id} | Customer: {customer_name}\n*Carrier:* {carrier_name} | Tracking: {tracking_number}\n*Status:* {tracking_status_tag} — {tracking_subtag}\n*Items:* {items_shipped}\n*Address:* {shipping_address}\n*Expected Delivery:* {aftership_expected_delivery}\n\nPlease investigate and contact the carrier if needed.",
        -220,
      ),

      // 8. Delivery confirmation Sheets
      sheetsNode(
        "8",
        "Log delivery tracking data (Sheets)",
        1480,
        "Shipment_Tracking",
        '[["{order_id}","{customer_name}","{customer_email}","{carrier_name}","{tracking_number}","{shipping_method}","{tracking_status_tag}","{tracking_subtag}","{aftership_expected_delivery}","{items_shipped}","{total_weight}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_2"),
      e("4", "7", "output_1"),
      e("5", "6"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1427. SLACK NOTIFICATION HUB (7 nodes)
  // ===================================================================

  "Slack Notification Hub": {
    summary:
      "Receives universal events via webhook, extracts event data with AI, classifies events by type and urgency in code, routes to the appropriate channel via condition, formats messages with AI for maximum clarity, deduplicates and throttles notifications in code, and delivers to Slack.",
    nodes: [
      trigger("Webhook: Universal event received", "webhook_app", {
        source: "event_aggregator",
        event: "event.received",
        howItStarts:
          "Fires when any event is received from integrated systems (CI/CD, monitoring, CRM, support, finance, HR) — acts as a universal notification hub that routes, formats, and deduplicates Slack messages",
        setup:
          "Configure webhook endpoints for each integrated system. Set OpenAI API key for event classification and message formatting. Set up Slack webhook(s) for each target channel. Define channel routing rules and deduplication windows.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI: Extract universal event data",
        280,
        "You are an event processing specialist. Extract the event details from the raw webhook payload regardless of format or source system. Determine the event source system, event type, severity, affected entity, description, timestamp, and any actionable data. Normalize the data into a consistent format. Respond JSON: {event_source: string, event_type: string, event_severity: string, affected_entity: string, event_description: string, event_timestamp: string, action_required: boolean, action_details: string, event_url: string, raw_event_id: string}",
        "Raw event payload: {webhook_payload}",
        [
          { field: "event_source", as: "event_source", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "event_severity", as: "event_severity", type: "string" },
          { field: "affected_entity", as: "affected_entity", type: "string" },
          {
            field: "event_description",
            as: "event_description",
            type: "string",
          },
          { field: "event_timestamp", as: "event_timestamp", type: "string" },
          { field: "action_required", as: "action_required", type: "boolean" },
          { field: "action_details", as: "action_details", type: "string" },
          { field: "event_url", as: "event_url", type: "string" },
          { field: "raw_event_id", as: "raw_event_id", type: "string" },
        ],
      ),

      // 3. Event classifier runCode
      n("3", "runCode", "Classify event and determine routing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify event type and determine Slack channel routing
const source = (vars.event_source || "unknown").toLowerCase();
const type = (vars.event_type || "generic").toLowerCase();
const severity = (vars.event_severity || "info").toUpperCase();
const channelMap = {
  deploy: "#deployments",
  incident: "#incidents",
  alert: "#alerts",
  sales: "#sales-notifications",
  support: "#support-queue",
  finance: "#finance-alerts",
  hr: "#hr-updates",
  security: "#security-alerts",
};
let targetChannel = "#general-notifications";
for (const [key, channel] of Object.entries(channelMap)) {
  if (source.includes(key) || type.includes(key)) { targetChannel = channel; break; }
}
if (severity === "CRITICAL" || severity === "EMERGENCY") targetChannel = "#critical-alerts";
const priorityLevel = severity === "CRITICAL" ? 1 : severity === "HIGH" ? 2 : severity === "MEDIUM" ? 3 : 4;
const eventFingerprint = source + ":" + type + ":" + vars.affected_entity;
const isHighPriority = priorityLevel <= 2;
return {
  target_channel: targetChannel,
  priority_level: priorityLevel,
  event_fingerprint: eventFingerprint,
  is_high_priority: isHighPriority,
  classification: severity + " " + type + " from " + source,
  classified_at: new Date().toISOString(),
};`,
          outputs: [
            { name: "target_channel", type: "string" },
            { name: "priority_level", type: "number" },
            { name: "event_fingerprint", type: "string" },
            { name: "is_high_priority", type: "boolean" },
            { name: "classification", type: "string" },
            { name: "classified_at", type: "string" },
          ],
        },
      }),

      // 4. Channel router condition
      n("4", "condition", "Check: Is event high priority?", 760, {
        conditionData: [
          { variable: "is_high_priority", operator: "equals", value: "true" },
        ],
      }),

      // 5. AI message formatter
      aiNode(
        "5",
        "AI: Format Slack notification message",
        1000,
        "You are a Slack notification designer. Create a well-formatted Slack message for this event. Use appropriate emoji based on severity (rotating_light for critical, warning for high, information_source for medium, white_check_mark for low/info). Include structured fields using Slack markdown. Keep it scannable — someone should understand the event in 3 seconds. Respond JSON: {formatted_message: string, thread_context: string}",
        "Source: {event_source}. Type: {event_type}. Severity: {event_severity}. Entity: {affected_entity}. Description: {event_description}. Action required: {action_required}. Action: {action_details}. Channel: {target_channel}. Classification: {classification}. URL: {event_url}.",
        [
          {
            field: "formatted_message",
            as: "formatted_message",
            type: "string",
          },
          { field: "thread_context", as: "thread_context", type: "string" },
        ],
      ),

      // 6. Deduplication throttle runCode
      n("6", "runCode", "Apply deduplication and throttle logic", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Deduplication and throttle logic
const fingerprint = vars.event_fingerprint || "";
const priority = parseInt(vars.priority_level || "4", 10);
// In production, this would check a Redis/DB cache for recent events with the same fingerprint
// For workflow purposes, we always allow high-priority events and throttle low-priority
const throttleWindowMinutes = priority <= 2 ? 0 : priority === 3 ? 15 : 60;
const shouldSend = true; // In production: check if fingerprint was seen within throttle window
const notificationId = "NOTIF-" + Date.now().toString(36).toUpperCase();
const finalMessage = vars.formatted_message + "\\n\\n_ID: " + notificationId + " | Channel: " + vars.target_channel + "_";
return {
  notification_id: notificationId,
  should_send: shouldSend,
  throttle_window: throttleWindowMinutes + " min",
  final_message: finalMessage,
  processed_at: new Date().toISOString(),
};`,
          outputs: [
            { name: "notification_id", type: "string" },
            { name: "should_send", type: "boolean" },
            { name: "throttle_window", type: "string" },
            { name: "final_message", type: "string" },
            { name: "processed_at", type: "string" },
          ],
        },
      }),

      // 7. Slack delivery
      slackNode("7", "Deliver notification to Slack", 1480, "{final_message}"),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
